import { api } from '../../core/network/api';
import type { AuthUser } from '../../core/state/auth-store';
import { mountCmsEditor } from './cms-editor';
import { downloadInvoiceAsAdmin, downloadInvoiceTemplate } from './file-download';

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function showToast(msg: string, kind: 'ok' | 'err' = 'ok') {
  const color = kind === 'ok'
    ? 'border-success/40 bg-success/15 text-success'
    : 'border-error/40 bg-error/15 text-error';
  const el = document.createElement('div');
  el.className = `fixed bottom-6 left-1/2 z-[400] -translate-x-1/2 rounded border ${color} px-4 py-2 font-label text-xs shadow-lg backdrop-blur transition-opacity duration-300`;
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => { el.style.opacity = '0'; }, 2300);
  setTimeout(() => { el.remove(); }, 2700);
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtCents(c: number, cur = 'EUR'): string {
  return (c / 100).toLocaleString('en-US', { style: 'currency', currency: cur, minimumFractionDigits: 2 });
}

function fmtUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function badge(status: string): string {
  const cls: Record<string, string> = {
    PENDING: 'bg-gold/15 text-gold',
    APPROVED: 'bg-success/15 text-success',
    REJECTED: 'bg-error/15 text-error',
    PAID: 'bg-success/15 text-success',
    COMPLETED: 'bg-success/15 text-success',
    FAILED: 'bg-error/15 text-error',
    NONE: 'bg-surface-2 text-text-dim',
  };
  return `<span class="inline-block rounded-sm px-2 py-0.5 font-label text-[10px] tracking-wider ${cls[status] ?? 'bg-surface-2 text-text-dim'}">${esc(status)}</span>`;
}

// ── Style constants ──────────────────────────────────────────────────────────

const TAB_IDLE = 'rounded-sm px-4 py-2 font-label text-[11px] tracking-wider text-text-dim transition hover:bg-gold/10 hover:text-text cursor-pointer';
const TAB_ACTIVE = 'rounded-sm px-4 py-2 font-label text-[11px] tracking-wider bg-gold text-bg cursor-pointer';
const STAT_CARD = 'rounded border border-gold/20 bg-surface p-4 text-center';
const STAT_NUM = 'mt-1 font-heading text-2xl text-gold tabular-nums';
const STAT_LABEL = 'font-label text-[10px] tracking-[.14em] uppercase text-text-dim';
const TH = 'border-b border-gold/20 px-3 py-2 text-left font-label text-[10px] tracking-[.12em] uppercase text-text-dim';
const TD = 'border-b border-border px-3 py-2.5 font-body text-sm text-text-dim';
const BTN = 'rounded-sm bg-gold px-3 py-1.5 font-label text-[11px] tracking-wider text-bg transition hover:brightness-110 disabled:opacity-50 cursor-pointer';
const BTN_DANGER = 'rounded-sm bg-crimson px-3 py-1.5 font-label text-[11px] tracking-wider text-text transition hover:brightness-110 disabled:opacity-50 cursor-pointer';
const BTN_OUTLINE = 'rounded-sm border border-gold/40 px-3 py-1.5 font-label text-[11px] tracking-wider text-text transition hover:border-gold hover:bg-gold/10 disabled:opacity-50 cursor-pointer';
const INPUT = 'w-full rounded-sm border border-gold/30 bg-bg px-3 py-2 font-body text-sm text-text outline-none focus:border-gold';
const INPUT_SM = 'rounded-sm border border-gold/30 bg-bg px-2 py-1.5 font-body text-xs text-text outline-none focus:border-gold';
const SECTION_TITLE = 'mb-3 font-heading text-lg text-gold';
const SUB_IDLE = 'rounded-sm px-3 py-1 font-body text-xs text-text-dim transition hover:text-text cursor-pointer';
const SUB_ACTIVE = 'rounded-sm px-3 py-1 font-body text-xs bg-gold/20 text-gold cursor-pointer';

// ── Types ────────────────────────────────────────────────────────────────────

interface Stats { totalUsers: number; newToday: number; newThisWeek: number; totalRevenue: number; pendingWithdrawals: number; pendingKyc: number }
interface ServerStatus { dbHealthy: boolean; uptimeSeconds: number; nodeVersion: string; memoryMb: number }
interface PlayerRow { id: string; email: string; emailVerified: boolean; username: string | null; role: string; banned: boolean; softBalance: number; premiumBalance: number; level: number; kycStatus: string; createdAt: string }
interface Paginated<T> { items: T[]; page: number; pageSize: number; total: number }
/** Admin list responses are `{ items, total, … }` envelopes; older builds returned bare arrays. */
type ListResponse<T> = T[] | { items: T[] };
const listItems = <T,>(r: ListResponse<T>): T[] => (Array.isArray(r) ? r : r.items ?? []);
interface WithdrawalRow { id: string; userId: string; amountCents: number; currency: string; status: string; email: string; reason: string | null; createdAt: string; user?: { username: string | null; email: string } }
interface InvoiceRow { id: string; number: string; type: string; status: string; amountCents: number; totalCents: number; currency: string; description: string; createdAt: string; user?: { username: string | null; email: string } }
interface TransactionRow { id: string; provider: string; providerTxId: string; /** major units */ amount: number; amountCents: number; currency: string; amountBase: number | null; status: string; createdAt: string; user?: { id: string; username: string | null; email: string } }
interface FaqRow { id: string; question: string; answer: string; sortOrder: number; active: boolean }
interface LegalRow { id: string; slug: string; title: string; content: string; active: boolean }
interface SocialRow { id: string; platform: string; url: string; icon: string; sortOrder: number; active: boolean }
interface NewsRow { id: string; title: string; summary: string; content: string; tag: string; published: boolean; slug: string; createdAt: string }
interface PaymentMethodRow { slug: string; name: string; icon: string; enabled: boolean; sortOrder: number }

// ── Modal ────────────────────────────────────────────────────────────────────

function adminModal(root: HTMLElement, title: string, bodyHtml: string, opts?: { dismissOnBackdrop?: boolean }): { body: HTMLDivElement; close: () => void } {
  const dismissOnBackdrop = opts?.dismissOnBackdrop ?? true;
  const backdrop = document.createElement('div');
  backdrop.className = 'fixed inset-0 z-[300] grid place-items-center px-5 py-10 bg-black/70 backdrop-blur-sm';
  backdrop.innerHTML = `<div class="relative w-full max-w-[520px] max-h-[80vh] overflow-y-auto rounded border-2 border-gold/50 bg-surface p-6 text-text" style="box-shadow:var(--shadow-lift)">
    <button type="button" data-modal-close class="absolute right-3.5 top-2.5 text-2xl leading-none text-text-dim transition hover:text-text cursor-pointer">&times;</button>
    <h2 class="mb-4 font-heading text-xl text-gold">${title}</h2>
    <div data-modal-body></div>
  </div>`;
  root.appendChild(backdrop);
  const body = backdrop.querySelector('[data-modal-body]') as HTMLDivElement;
  body.innerHTML = bodyHtml;
  const close = () => backdrop.remove();
  backdrop.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-modal-close]')) close();
    else if (dismissOnBackdrop && t === backdrop) close();
  });
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); } };
  document.addEventListener('keydown', onKey);
  return { body, close };
}

// ── Main ─────────────────────────────────────────────────────────────────────

const TABS = ['Dashboard', 'Players', 'Transactions', 'KYC', 'Withdrawals', 'Invoices', 'CMS', 'Seasons', 'Meta', 'API Keys', 'Settings', 'Server'] as const;
type TabName = (typeof TABS)[number];
type ActiveTab = TabName | 'Sandbox';

const SLUG_TO_TAB: Record<string, TabName> = {
  dashboard: 'Dashboard', players: 'Players', transactions: 'Transactions', kyc: 'KYC',
  withdrawals: 'Withdrawals', invoices: 'Invoices', cms: 'CMS',
  seasons: 'Seasons', meta: 'Meta', 'api-keys': 'API Keys',
  settings: 'Settings', server: 'Server',
};
function tabToSlug(tab: ActiveTab): string {
  return tab.toLowerCase().replace(/\s+/g, '-');
}

export function mountAdmin(root: HTMLElement, _user: AuthUser): () => void {
  let disposed = false;
  let sandboxMode = window.location.pathname === '/admin-panel/sandbox';
  let sandboxData: any = null;
  let dashRefreshTimer: ReturnType<typeof setInterval> | undefined;
  let dashLastLoad = 0;
  let dashAgoTimer: ReturnType<typeof setInterval> | undefined;

  let activeTab: ActiveTab = 'Dashboard';
  // Set by the Players tab's "Transactions" action so the Transactions tab
  // opens already filtered to that player's email.
  let txPrefillSearch = '';
  if (sandboxMode) {
    activeTab = 'Sandbox';
  } else {
    const hash = location.hash.replace('#', '');
    if (hash && SLUG_TO_TAB[hash]) activeTab = SLUG_TO_TAB[hash];
  }

  const tabBar = () => TABS.map((t) =>
    `<button type="button" data-tab="${t}" class="${t === activeTab ? TAB_ACTIVE : TAB_IDLE}">${t}</button>`
  ).join('');

  root.innerHTML = `
  <div class="min-h-screen w-full bg-bg font-body text-text">
    <header class="border-b border-border bg-surface">
      <div class="mx-auto flex max-w-[1280px] items-center justify-between px-6 py-3">
        <h1 class="font-heading text-2xl text-gold">Admin Panel</h1>
        <a href="/play" class="${BTN_OUTLINE}">Back to Game</a>
      </div>
    </header>
    <main class="mx-auto max-w-[1280px] px-6 pb-20">
      <div id="adm-tabs" class="mt-5 flex flex-wrap gap-1.5 rounded bg-surface/60 p-1.5">${tabBar()}</div>
      <div id="adm-content" class="mt-6"></div>
    </main>
  </div>`;

  const content = root.querySelector('#adm-content') as HTMLDivElement;
  const tabsEl = root.querySelector('#adm-tabs') as HTMLDivElement;
  if (sandboxMode) tabsEl.style.display = 'none';

  function switchTab(tab: ActiveTab, pushState = true) {
    activeTab = tab;
    if (pushState && !sandboxMode) {
      const slug = tabToSlug(tab);
      history.pushState(null, '', '#' + slug);
    }
    tabsEl.innerHTML = tabBar();
    renderTab();
  }

  tabsEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('[data-tab]') as HTMLElement | null;
    if (btn) switchTab(btn.dataset.tab as TabName);
  });

  function renderTab() {
    if (disposed) return;
    clearInterval(dashRefreshTimer);
    clearInterval(dashAgoTimer);
    content.innerHTML = '<div class="grid h-40 place-items-center font-body text-sm text-text-dim">Loading...</div>';
    switch (activeTab) {
      case 'Dashboard': renderDashboard(); break;
      case 'Sandbox': renderSandbox(); break;
      case 'Players': renderPlayers(); break;
      case 'Transactions': renderTransactions(); break;
      case 'KYC': renderKyc(); break;
      case 'Withdrawals': renderWithdrawals(); break;
      case 'Invoices': renderInvoices(); break;
      case 'CMS': renderCms(); break;
      case 'Seasons': renderSeasons(); break;
      case 'Meta': renderMeta(); break;
      case 'API Keys': renderApiKeys(); break;
      case 'Settings': renderSettings(); break;
      case 'Server': renderServer(); break;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SEASONS
  // ═══════════════════════════════════════════════════════════════════════════

  interface SeasonRow {
    id: string;
    name: string;
    number: number;
    status: string;
    startsAt: string;
    endsAt: string;
    rewards: Array<{ tierName: string; minPoints: number; rewardSoft: number; rewardPremium: number; rewardXp: number }>;
  }

  function seasonBadge(status: string): string {
    const cls: Record<string, string> = {
      UPCOMING: 'bg-blue-500/15 text-blue-400',
      ACTIVE: 'bg-success/15 text-success',
      ENDED: 'bg-surface-2 text-text-dim',
    };
    return `<span class="inline-block rounded-sm px-2 py-0.5 font-label text-[10px] tracking-wider ${cls[status] ?? 'bg-surface-2 text-text-dim'}">${esc(status)}</span>`;
  }

  function renderSeasons() {
    content.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';

    function load() {
      api.get<SeasonRow[]>('/admin/seasons').then((seasons) => {
        if (disposed) return;
        content.innerHTML = `
          <div class="mb-4 flex items-center justify-between">
            <h2 class="${SECTION_TITLE}">Seasons</h2>
            <button id="s-create" class="${BTN}">+ Create Season</button>
          </div>
          <div id="s-table" class="overflow-x-auto"></div>`;

        const tbl = content.querySelector('#s-table') as HTMLDivElement;
        if (seasons.length === 0) {
          tbl.innerHTML = '<div class="py-6 text-center text-text-dim">No seasons yet</div>';
        } else {
          tbl.innerHTML = `<table class="w-full text-left"><thead><tr>
            <th class="${TH}">#</th>
            <th class="${TH}">Name</th>
            <th class="${TH}">Status</th>
            <th class="${TH}">Starts</th>
            <th class="${TH}">Ends</th>
            <th class="${TH}">Tiers</th>
            <th class="${TH}">Actions</th>
          </tr></thead><tbody>${seasons.map((s) => `<tr>
            <td class="${TD}">${s.number}</td>
            <td class="${TD}">${esc(s.name)}</td>
            <td class="${TD}">${seasonBadge(s.status)}</td>
            <td class="${TD}">${fmtDate(s.startsAt)}</td>
            <td class="${TD}">${fmtDate(s.endsAt)}</td>
            <td class="${TD}">${s.rewards?.length ?? 0}</td>
            <td class="${TD}">
              <div class="flex gap-1.5">
                ${s.status === 'UPCOMING' ? `<button data-s-activate="${s.id}" class="${BTN}">Activate</button>` : ''}
                ${s.status === 'ACTIVE' ? `<button data-s-end="${s.id}" class="${BTN_DANGER}">End</button>` : ''}
                ${s.status === 'UPCOMING' ? `<button data-s-edit="${s.id}" class="${BTN_OUTLINE}">Edit</button>` : ''}
                ${s.status === 'UPCOMING' ? `<button data-s-del="${s.id}" class="${BTN_DANGER}">Delete</button>` : ''}
              </div>
            </td>
          </tr>`).join('')}</tbody></table>`;
        }

        content.querySelector('#s-create')!.addEventListener('click', () => openSeasonModal());

        tbl.addEventListener('click', (e) => {
          const t = e.target as HTMLElement;
          const activateBtn = t.closest('[data-s-activate]') as HTMLElement | null;
          const endBtn = t.closest('[data-s-end]') as HTMLElement | null;
          const editBtn = t.closest('[data-s-edit]') as HTMLElement | null;
          const delBtn = t.closest('[data-s-del]') as HTMLElement | null;

          if (activateBtn) {
            const id = activateBtn.dataset.sActivate!;
            const { body, close } = adminModal(root, 'Activate Season', `
              <p class="mb-4 font-body text-sm text-text-dim">Only one season can be active at a time.</p>
              <div class="flex justify-end gap-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN}">Activate</button></div>`);
            body.querySelector('[data-confirm]')!.addEventListener('click', () => {
              (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
              api.post(`/admin/seasons/${id}/activate`, {})
                .then(() => { showToast('Season activated'); close(); load(); })
                .catch((err: any) => { showToast(err?.response?.data?.message ?? 'Failed', 'err'); (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = false; });
            });
          }

          if (endBtn) {
            const id = endBtn.dataset.sEnd!;
            const { body, close } = adminModal(root, 'End Season', `
              <p class="mb-4 font-body text-sm text-text-dim">This will distribute rewards and reset rank points. Cannot be undone.</p>
              <div class="flex justify-end gap-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN_DANGER}">End Season</button></div>`);
            body.querySelector('[data-confirm]')!.addEventListener('click', () => {
              (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
              api.post(`/admin/seasons/${id}/end`, {})
                .then(() => { showToast('Season ended'); close(); load(); })
                .catch((err: any) => { showToast(err?.response?.data?.message ?? 'Failed', 'err'); (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = false; });
            });
          }

          if (editBtn) {
            const id = editBtn.dataset.sEdit!;
            const season = seasons.find((s) => s.id === id);
            if (season) openSeasonModal(season);
          }

          if (delBtn) {
            const id = delBtn.dataset.sDel!;
            const { body, close } = adminModal(root, 'Delete Season', `
              <p class="mb-4 font-body text-sm text-text-dim">This cannot be undone.</p>
              <div class="flex justify-end gap-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN_DANGER}">Delete</button></div>`);
            body.querySelector('[data-confirm]')!.addEventListener('click', () => {
              (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
              api.del(`/admin/seasons/${id}`)
                .then(() => { showToast('Season deleted'); close(); load(); })
                .catch(() => { showToast('Failed to delete', 'err'); (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = false; });
            });
          }
        });
      }).catch(() => {
        content.innerHTML = '<div class="py-6 text-center text-error">Failed to load seasons</div>';
      });
    }

    function openSeasonModal(existing?: SeasonRow) {
      const isEdit = !!existing;
      const tiers = existing?.rewards ?? [{ tierName: 'Bronze', minPoints: 0, rewardSoft: 100, rewardPremium: 0, rewardXp: 50 }];

      const tierRowHtml = (t: { tierName: string; minPoints: number; rewardSoft: number; rewardPremium: number; rewardXp: number }, idx: number) => `
        <div class="flex items-center gap-2 tier-row" data-tier-idx="${idx}">
          <input data-tier-name type="text" value="${esc(t.tierName)}" placeholder="Tier name" class="${INPUT} w-24" />
          <input data-tier-pts type="number" min="0" value="${t.minPoints}" placeholder="Min pts" class="${INPUT} w-20" />
          <input data-tier-soft type="number" min="0" value="${t.rewardSoft}" placeholder="Soft" class="${INPUT} w-20" />
          <input data-tier-prem type="number" min="0" value="${t.rewardPremium}" placeholder="Premium" class="${INPUT} w-20" />
          <input data-tier-xp type="number" min="0" value="${t.rewardXp}" placeholder="XP" class="${INPUT} w-20" />
          <button type="button" data-remove-tier="${idx}" class="text-error hover:brightness-125 text-lg leading-none cursor-pointer">&times;</button>
        </div>`;

      const { body, close } = adminModal(root, isEdit ? 'Edit Season' : 'Create Season', `
        <div class="space-y-3">
          <div><label class="${STAT_LABEL}">NAME</label><input data-sname type="text" value="${esc(existing?.name ?? '')}" class="${INPUT} mt-1" placeholder="Season name" /></div>
          <div class="grid grid-cols-2 gap-3">
            <div><label class="${STAT_LABEL}">START DATE</label><input data-starts type="datetime-local" value="${existing ? new Date(existing.startsAt).toISOString().slice(0, 16) : ''}" class="${INPUT} mt-1" /></div>
            <div><label class="${STAT_LABEL}">END DATE</label><input data-ends type="datetime-local" value="${existing ? new Date(existing.endsAt).toISOString().slice(0, 16) : ''}" class="${INPUT} mt-1" /></div>
          </div>
          <div>
            <label class="${STAT_LABEL}">REWARD TIERS</label>
            <div id="tier-list" class="mt-1 space-y-2">${tiers.map((t, i) => tierRowHtml(t, i)).join('')}</div>
            <button type="button" id="add-tier" class="${BTN_OUTLINE} mt-2">+ Add Tier</button>
          </div>
          <div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN}">${isEdit ? 'Save' : 'Create'}</button></div>
        </div>`, { dismissOnBackdrop: false });

      let tierCount = tiers.length;

      body.querySelector('#add-tier')!.addEventListener('click', () => {
        const list = body.querySelector('#tier-list') as HTMLDivElement;
        const div = document.createElement('div');
        div.innerHTML = tierRowHtml({ tierName: '', minPoints: 0, rewardSoft: 0, rewardPremium: 0, rewardXp: 0 }, tierCount++);
        list.appendChild(div.firstElementChild!);
      });

      body.querySelector('#tier-list')!.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest('[data-remove-tier]');
        if (btn) btn.closest('.tier-row')?.remove();
      });

      body.querySelector('[data-confirm]')!.addEventListener('click', () => {
        const name = (body.querySelector('[data-sname]') as HTMLInputElement).value.trim();
        const startsAt = (body.querySelector('[data-starts]') as HTMLInputElement).value;
        const endsAt = (body.querySelector('[data-ends]') as HTMLInputElement).value;

        if (!name) { showToast('Name is required', 'err'); return; }
        if (!startsAt || !endsAt) { showToast('Both dates are required', 'err'); return; }

        const tierRows = body.querySelectorAll('.tier-row');
        const tierData: Array<{ tierName: string; minPoints: number; rewardSoft: number; rewardPremium: number; rewardXp: number }> = [];
        tierRows.forEach((row) => {
          tierData.push({
            tierName: (row.querySelector('[data-tier-name]') as HTMLInputElement).value.trim(),
            minPoints: parseInt((row.querySelector('[data-tier-pts]') as HTMLInputElement).value, 10) || 0,
            rewardSoft: parseInt((row.querySelector('[data-tier-soft]') as HTMLInputElement).value, 10) || 0,
            rewardPremium: parseInt((row.querySelector('[data-tier-prem]') as HTMLInputElement).value, 10) || 0,
            rewardXp: parseInt((row.querySelector('[data-tier-xp]') as HTMLInputElement).value, 10) || 0,
          });
        });

        if (tierData.length === 0) { showToast('Add at least one tier', 'err'); return; }
        if (tierData.some((t) => !t.tierName)) { showToast('All tiers need a name', 'err'); return; }

        const btn = body.querySelector('[data-confirm]') as HTMLButtonElement;
        btn.disabled = true;

        const payload = { name, startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString(), tiers: tierData };

        const req = isEdit
          ? api.patch(`/admin/seasons/${existing!.id}`, payload)
          : api.post('/admin/seasons', payload);

        req.then(() => { showToast(isEdit ? 'Season updated' : 'Season created'); close(); load(); })
          .catch((err: any) => { showToast(err?.response?.data?.message ?? 'Failed', 'err'); btn.disabled = false; });
      });
    }

    load();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  function editable(val: string | number): string {
    if (!sandboxMode) return esc(String(val));
    return `<span class="cursor-pointer" data-sandbox-edit>${esc(String(val))}</span>`;
  }

  function panelTitle(text: string): string {
    return `<div class="mb-3 font-heading text-base text-gold">${editable(text)}</div>`;
  }

  function econRow(label: string, val: string, color: string): string {
    return `<div class="flex items-center justify-between py-1.5 border-b border-border last:border-0"><span class="font-body text-sm text-text-dim">${editable(label)}</span><span class="font-heading text-sm tabular-nums" style="color:${color}">${editable(val)}</span></div>`;
  }

  function dashStatCard(val: string | number, label: string, sub?: string, icon?: string): string {
    const iconHtml = icon ? `<div class="text-2xl mb-1">${icon}</div>` : '';
    const subHtml = sub ? `<div class="mt-1 font-body text-[11px] text-text-dim">${sandboxMode ? editable(sub) : sub}</div>` : '';
    return `<div class="${STAT_CARD}">${iconHtml}<div class="${STAT_NUM}">${editable(val)}</div><div class="${STAT_LABEL}">${editable(label)}</div>${subHtml}</div>`;
  }

  function dashTimeAgo(iso: string): string {
    if (!iso) return '—';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return s + 's ago';
    if (s < 3600) return Math.floor(s / 60) + 'm ago';
    if (s < 86400) return Math.floor(s / 3600) + 'h ago';
    return Math.floor(s / 86400) + 'd ago';
  }

  function fmtNum(n: number): string {
    return n.toLocaleString('en-US');
  }

  function wireEditables(container: HTMLElement) {
    container.querySelectorAll<HTMLSpanElement>('[data-sandbox-edit]').forEach((el) => {
      el.addEventListener('click', () => {
        if (el.querySelector('input')) return;
        const val = el.textContent || '';
        const w = Math.max(el.offsetWidth + 16, 50);
        const inp = document.createElement('input');
        inp.className = 'bg-transparent border-none outline-none text-inherit font-inherit';
        inp.value = val;
        inp.style.width = w + 'px';
        el.textContent = '';
        el.appendChild(inp);
        inp.focus();
        inp.select();
        inp.addEventListener('blur', () => { el.textContent = inp.value; });
        inp.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { inp.blur(); }
          if (e.key === 'Escape') { el.textContent = val; }
        });
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════

  // Overview response matches the nested shape from GET /admin/stats/overview
  // Falls back to flat GET /admin/stats if overview is unavailable

  function renderDashboard() {
    clearInterval(dashRefreshTimer);
    clearInterval(dashAgoTimer);

    content.innerHTML = `<div class="space-y-6">
      <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
        ${Array(4).fill(0).map(() => `<div class="${STAT_CARD}"><div class="${STAT_LABEL}">...</div><div class="${STAT_NUM}">—</div></div>`).join('')}
      </div>
      <div class="grid h-40 place-items-center font-body text-sm text-text-dim">Loading dashboard...</div>
    </div>`;

    Promise.all([
      api.get<any>('/admin/stats/overview').catch(() => api.get<Stats>('/admin/stats').catch(() => null)),
      api.get<ServerStatus>('/admin/server-status').catch(() => null),
      api.get<Record<string, string>>('/admin/settings').catch(() => ({} as Record<string, string>)),
    ]).then(([rawStats, server, settings]) => {
      if (disposed) return;

      if (sandboxMode && !sandboxData) {
        sandboxData = JSON.parse(JSON.stringify({ overview: rawStats, server, settings }));
      }

      const d = (sandboxMode && sandboxData) ? sandboxData.overview : rawStats;
      const srv = (sandboxMode && sandboxData) ? sandboxData.server : server;
      const sett = (sandboxMode && sandboxData) ? sandboxData.settings : settings;

      if (!d) {
        content.innerHTML = '<div class="py-10 text-center text-error font-body">Failed to load dashboard data</div>';
        return;
      }

      dashLastLoad = Math.floor(Date.now() / 1000);

      // Handle nested (overview) or flat (legacy stats) response
      const isOverview = 'players' in d && 'economy' in d;
      const totalUsers = isOverview ? d.players.total : (d.totalUsers ?? 0);
      const newToday = isOverview ? d.players.newToday : (d.newToday ?? 0);
      const newWeek = isOverview ? d.players.newThisWeek : (d.newThisWeek ?? 0);
      const battlesToday = isOverview ? d.combat?.battles24h ?? 0 : 0;
      const victories = isOverview ? d.combat?.victories24h ?? 0 : 0;
      const defeats = isOverview ? d.combat?.defeats24h ?? 0 : 0;
      const totalCash = isOverview ? d.economy?.totalCash ?? 0 : 0;
      const totalGold = isOverview ? d.economy?.totalGold ?? 0 : 0;
      const avgCash = isOverview ? d.economy?.avgCashPerPlayer ?? 0 : 0;
      const cashFaucet = isOverview ? d.economy?.cashFaucet24h ?? 0 : 0;
      const cashSink = isOverview ? d.economy?.cashSink24h ?? 0 : 0;
      const netFlow = isOverview ? d.economy?.netFlow24h ?? 0 : 0;
      const pendingKyc = isOverview ? d.pending?.kyc ?? 0 : (d.pendingKyc ?? 0);
      const pendingWd = isOverview ? d.pending?.withdrawals ?? 0 : (d.pendingWithdrawals ?? 0);
      const revenueCents = isOverview ? d.revenue?.totalCents ?? 0 : (d.totalRevenue ?? 0);
      const revenueTodayCents = isOverview ? d.revenue?.todayCents ?? 0 : 0;
      const recentPlayers = d.recentPlayers ?? [];
      const recentBattles = d.recentBattles ?? [];
      const totalHeroes = isOverview ? d.heroes?.total ?? 0 : 0;
      const classCounts = isOverview ? { WARRIOR: d.heroes?.warriors ?? 0, MAGE: d.heroes?.mages ?? 0, ARCHER: d.heroes?.archers ?? 0, PALADIN: d.heroes?.paladins ?? 0 } : {};
      const legendaryCount = isOverview ? d.artifacts?.legendary ?? 0 : 0;
      const activeListings = isOverview ? d.market?.activeListings ?? 0 : 0;
      const heistsToday = isOverview ? d.heists?.total ?? 0 : 0;
      const turfControlled = isOverview ? d.territories?.controlled ?? 0 : 0;
      const turfTotal = isOverview ? d.territories?.total ?? 0 : 0;
      const mobKills = isOverview ? d.combat?.mobKills ?? 0 : 0;
      const pvpMatches = isOverview ? d.combat?.pvpMatches ?? 0 : 0;
      const highestElo = isOverview ? d.combat?.highestElo ?? 0 : 0;
      const heistSuccess = isOverview ? d.heists?.successRate ?? 0 : 0;
      const epicCount = isOverview ? d.artifacts?.epic ?? 0 : 0;
      const brokenCount = isOverview ? d.artifacts?.broken ?? 0 : 0;
      const sold24h = isOverview ? d.market?.sold24h ?? 0 : 0;
      const volume24h = isOverview ? d.market?.volume24h ?? 0 : 0;
      const chatMessages = isOverview ? d.social?.chatMessages24h ?? 0 : 0;
      const referrals = isOverview ? d.social?.referrals24h ?? 0 : 0;

      const srvUp = srv ? fmtUptime(srv.uptimeSeconds) : '—';
      const srvDb = srv ? (srv.dbHealthy ? 'Healthy' : 'Down') : '—';
      const srvMem = srv ? srv.memoryMb + ' MB' : '—';
      const srvNode = srv ? srv.nodeVersion : '—';

      const g = (k: string) => (sett as Record<string, string>)?.[k] ?? '';
      const b = (k: string) => g(k) === 'true' || g(k) === '1';

      const resultBadge = (r: string) => r === 'VICTORY'
        ? '<span class="inline-block rounded-sm px-2 py-0.5 font-label text-[10px] tracking-wider bg-success/15 text-success">WIN</span>'
        : '<span class="inline-block rounded-sm px-2 py-0.5 font-label text-[10px] tracking-wider bg-error/15 text-error">LOSS</span>';

      const sourceBadge = (s: string) => {
        const map: Record<string, string> = { mob: 'bg-gold/15 text-gold', territory: 'bg-blue-500/15 text-blue-400', dungeon: 'bg-purple-500/15 text-purple-400' };
        const cls = map[s] ?? 'bg-surface-2 text-text-dim';
        return `<span class="inline-block rounded-sm px-2 py-0.5 font-label text-[10px] tracking-wider ${cls}">${esc(s.toUpperCase())}</span>`;
      };

      let html = '';

      // ── Top bar ──
      html += `<div class="flex items-center justify-between mb-5">
        <div class="flex items-center gap-3">
          <span id="dash-title" class="font-heading text-xl text-gold ${sandboxMode ? 'cursor-pointer hover:text-gold-bright' : ''}">${editable('Command Center')}</span>
          ${sandboxMode ? '' : '<span id="dash-ago" class="font-label text-[10px] tracking-wider text-text-dim/50">0s ago</span>'}
        </div>
      </div>`;

      // ── Stat cards row ──
      html += `<div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
        ${dashStatCard(fmtNum(battlesToday), 'Hits Today', `<span class="text-success">${victories} W</span> / <span class="text-error">${defeats} L</span>`, '&#x2694;&#xFE0F;')}
        ${dashStatCard(fmtNum(totalUsers), 'Total Players', `+${newToday} today, +${newWeek} this week`, '&#x1F6E1;&#xFE0F;')}
        ${dashStatCard(fmtNum(totalCash), 'Cash Circulating', `Avg ${fmtNum(avgCash)} per player`, '&#x1F4B0;')}
        ${dashStatCard(fmtCents(revenueCents), 'Revenue', `Today: ${fmtCents(revenueTodayCents)}`, '&#x1F4B5;')}
      </div>`;

      // ── Two-col: class breakdown + economy ──
      html += `<div class="grid grid-cols-1 gap-4 lg:grid-cols-2 mt-6">`;

      // Left: Class breakdown + game stats
      html += `<div class="rounded border border-gold/20 bg-surface p-4">
        ${panelTitle('Outfit Roster')}
        <div class="grid grid-cols-2 gap-3 mb-4">
          <div class="rounded border border-gold/10 bg-bg/50 p-2.5 text-center"><div class="font-label text-[10px] tracking-wider text-text-dim">ENFORCERS</div><div class="font-heading text-lg text-gold tabular-nums">${editable(classCounts.WARRIOR ?? 0)}</div></div>
          <div class="rounded border border-gold/10 bg-bg/50 p-2.5 text-center"><div class="font-label text-[10px] tracking-wider text-text-dim">FIXERS</div><div class="font-heading text-lg text-gold tabular-nums">${editable(classCounts.MAGE ?? 0)}</div></div>
          <div class="rounded border border-gold/10 bg-bg/50 p-2.5 text-center"><div class="font-label text-[10px] tracking-wider text-text-dim">TRIGGERMEN</div><div class="font-heading text-lg text-gold tabular-nums">${editable(classCounts.ARCHER ?? 0)}</div></div>
          <div class="rounded border border-gold/10 bg-bg/50 p-2.5 text-center"><div class="font-label text-[10px] tracking-wider text-text-dim">CAPOS</div><div class="font-heading text-lg text-gold tabular-nums">${editable(classCounts.PALADIN ?? 0)}</div></div>
        </div>
        <div class="space-y-0">
          ${econRow('Total Heroes', fmtNum(totalHeroes), 'var(--color-gold, #c9a44a)')}
          ${econRow('Mob Kills', fmtNum(mobKills), '#e0662b')}
          ${econRow('Pit Fights (PvP)', fmtNum(pvpMatches), '#e04040')}
          ${econRow('Highest ELO', fmtNum(highestElo), '#b184d6')}
          ${econRow('Heists (all time)', fmtNum(heistsToday), '#e0662b')}
          ${econRow('Heist Success', Math.round(heistSuccess * 100) + '%', '#6fae4b')}
          ${econRow('Turf Controlled', `${turfControlled} / ${turfTotal}`, '#6fae4b')}
        </div>
      </div>`;

      // Right: Economy panel
      html += `<div class="rounded border border-gold/20 bg-surface p-4">
        ${panelTitle('Economy')}
        <div class="space-y-0">
          ${econRow('Total Cash', fmtNum(totalCash), 'var(--color-gold, #c9a44a)')}
          ${econRow('Total Gold Bars', fmtNum(totalGold), 'var(--color-gold, #c9a44a)')}
          ${econRow('Avg Cash / Player', fmtNum(avgCash), 'var(--color-gold, #c9a44a)')}
          ${econRow('Cash Faucet (24h)', '+' + fmtNum(cashFaucet), '#6fae4b')}
          ${econRow('Cash Sink (24h)', '-' + fmtNum(cashSink), '#e04040')}
          ${econRow('Net Flow (24h)', (netFlow >= 0 ? '+' : '') + fmtNum(netFlow), netFlow >= 0 ? '#6fae4b' : '#e04040')}
          ${econRow('Legendary Gear', fmtNum(legendaryCount), '#b184d6')}
          ${econRow('Epic Gear', fmtNum(epicCount), '#6f8fae')}
          ${econRow('Broken Items', fmtNum(brokenCount), brokenCount > 0 ? '#e04040' : 'var(--color-text-dim, #888)')}
          ${econRow('Market Listings', fmtNum(activeListings), 'var(--color-gold, #c9a44a)')}
          ${econRow('Sold (24h)', fmtNum(sold24h), '#6fae4b')}
          ${econRow('Market Vol (24h)', fmtNum(volume24h), 'var(--color-gold, #c9a44a)')}
          ${econRow('Chat (24h)', fmtNum(chatMessages), 'var(--color-text-dim, #888)')}
          ${econRow('Referrals (24h)', fmtNum(referrals), 'var(--color-text-dim, #888)')}
          ${econRow('Pending KYC', String(pendingKyc), pendingKyc > 0 ? '#e0662b' : 'var(--color-text-dim, #888)')}
          ${econRow('Pending Withdrawals', String(pendingWd), pendingWd > 0 ? '#e0662b' : 'var(--color-text-dim, #888)')}
        </div>
      </div>`;
      html += `</div>`;

      // ── Two-col: Recent recruits + recent battles ──
      html += `<div class="grid grid-cols-1 gap-4 lg:grid-cols-2 mt-4">`;

      // Recent recruits
      html += `<div class="rounded border border-gold/20 bg-surface p-4">
        ${panelTitle('Recent Recruits')}
        ${recentPlayers.length === 0 ? '<div class="py-4 text-center text-text-dim font-body text-sm">No players yet</div>' : `
        <div class="overflow-x-auto"><table class="w-full text-left"><thead><tr>
          <th class="${TH}">Name</th><th class="${TH}">Level</th><th class="${TH}">Cash</th><th class="${TH}">Joined</th>
        </tr></thead><tbody>${recentPlayers.slice(0, 10).map((p: any) => `<tr>
          <td class="${TD}">${editable(p.username ?? '—')}</td>
          <td class="${TD}">${editable('Lv.' + p.level)}</td>
          <td class="${TD} tabular-nums" style="color:var(--color-gold, #c9a44a)">${editable(fmtNum(p.softBalance))}</td>
          <td class="${TD} text-text-dim">${dashTimeAgo(p.createdAt)}</td>
        </tr>`).join('')}</tbody></table></div>`}
      </div>`;

      // Recent battles
      html += `<div class="rounded border border-gold/20 bg-surface p-4">
        ${panelTitle('Recent Hits')}
        ${recentBattles.length === 0 ? '<div class="py-4 text-center text-text-dim font-body text-sm">No battles yet</div>' : `
        <div class="overflow-x-auto"><table class="w-full text-left"><thead><tr>
          <th class="${TH}">Source</th><th class="${TH}">Result</th><th class="${TH}">Cash</th><th class="${TH}">XP</th><th class="${TH}">When</th>
        </tr></thead><tbody>${recentBattles.slice(0, 10).map((b: any) => `<tr>
          <td class="${TD}">${sourceBadge(b.source)}</td>
          <td class="${TD}">${resultBadge(b.result)}</td>
          <td class="${TD} tabular-nums" style="color:var(--color-gold, #c9a44a)">${editable('+' + fmtNum(b.cashEarned))}</td>
          <td class="${TD} tabular-nums text-text-dim">${editable('+' + fmtNum(b.xpEarned))}</td>
          <td class="${TD} text-text-dim">${dashTimeAgo(b.createdAt)}</td>
        </tr>`).join('')}</tbody></table></div>`}
      </div>`;
      html += `</div>`;

      // ── Feature toggles ──
      if (isOverview) {
        const toggle = (key: string, label: string, on: boolean) =>
          `<label class="flex items-center justify-between rounded border border-gold/10 bg-bg/50 px-4 py-2.5 cursor-pointer"><span class="font-body text-sm text-text">${editable(label)}</span><input type="checkbox" data-dash-ft="${key}" ${on ? 'checked' : ''} class="h-4 w-4 accent-gold" ${sandboxMode ? '' : ''} /></label>`;
        html += `<div class="rounded border border-gold/20 bg-surface p-4 mt-4">
          ${panelTitle('Feature Toggles')}
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
            ${toggle('feature.kyc', 'KYC Verification', b('feature.kyc'))}
            ${toggle('feature.withdrawals', 'Withdrawals', b('feature.withdrawals'))}
            ${toggle('feature.invoices', 'Invoices', b('feature.invoices'))}
          </div>
        </div>`;
      }

      // ── Server status ──
      html += `<div class="rounded border border-gold/20 bg-surface p-4 mt-4">
        ${panelTitle('Server Status')}
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div class="rounded border border-gold/10 bg-bg/50 p-3 text-center"><div class="${STAT_LABEL}">DB STATUS</div><div class="mt-1 font-heading text-lg ${srv?.dbHealthy ? 'text-success' : 'text-error'}">${editable(srvDb)}</div></div>
          <div class="rounded border border-gold/10 bg-bg/50 p-3 text-center"><div class="${STAT_LABEL}">UPTIME</div><div class="mt-1 font-heading text-lg text-gold tabular-nums">${editable(srvUp)}</div></div>
          <div class="rounded border border-gold/10 bg-bg/50 p-3 text-center"><div class="${STAT_LABEL}">MEMORY</div><div class="mt-1 font-heading text-lg text-gold tabular-nums">${editable(srvMem)}</div></div>
          <div class="rounded border border-gold/10 bg-bg/50 p-3 text-center"><div class="${STAT_LABEL}">NODE</div><div class="mt-1 font-heading text-lg text-gold tabular-nums">${editable(srvNode)}</div></div>
        </div>
      </div>`;

      content.innerHTML = html;

      if (sandboxMode) {
        wireEditables(content);
        const titleEl = content.querySelector('#dash-title');
        if (titleEl) {
          titleEl.addEventListener('click', () => { sandboxData = null; renderDashboard(); });
        }
      }

      content.querySelectorAll<HTMLInputElement>('[data-dash-ft]').forEach((cb) => {
        if (sandboxMode) {
          cb.addEventListener('change', () => {});
        } else {
          cb.addEventListener('change', () => {
            api.post('/admin/settings', { key: cb.dataset.dashFt!, value: cb.checked ? 'true' : 'false' })
              .then(() => showToast(`${cb.dataset.dashFt} updated`))
              .catch(() => { showToast('Failed', 'err'); cb.checked = !cb.checked; });
          });
        }
      });

      if (!sandboxMode) {
        dashRefreshTimer = setInterval(() => { renderDashboard(); }, 30000);
        dashAgoTimer = setInterval(() => {
          const agoEl = content.querySelector('#dash-ago');
          if (agoEl) {
            const s = Math.floor(Date.now() / 1000) - dashLastLoad;
            agoEl.textContent = s + 's ago';
          }
        }, 1000);
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SANDBOX
  // ═══════════════════════════════════════════════════════════════════════════

  function renderSandbox() {
    sandboxMode = true;
    renderDashboard();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PLAYERS
  // ═══════════════════════════════════════════════════════════════════════════

  function renderPlayers() {
    let page = 1, search = '', statusFilter = 'all';

    function render() {
      content.innerHTML = `
        <div class="mb-4 flex flex-wrap items-center gap-3">
          <input id="p-search" type="text" placeholder="Search users..." value="${esc(search)}" class="${INPUT} max-w-xs" />
          <select id="p-status" class="${INPUT_SM} w-28">
            <option value="all" ${statusFilter === 'all' ? 'selected' : ''}>All</option>
            <option value="active" ${statusFilter === 'active' ? 'selected' : ''}>Active</option>
            <option value="banned" ${statusFilter === 'banned' ? 'selected' : ''}>Banned</option>
          </select>
          <button id="p-create-user" class="${BTN} ml-auto">+ Create User</button>
        </div>
        <div id="p-table" class="overflow-x-auto"></div>
        <div id="p-pag" class="mt-3 flex items-center gap-3"></div>`;
      const searchEl = content.querySelector('#p-search') as HTMLInputElement;
      const statusEl = content.querySelector('#p-status') as HTMLSelectElement;
      let debounce: ReturnType<typeof setTimeout>;
      searchEl.addEventListener('input', () => { clearTimeout(debounce); debounce = setTimeout(() => { search = searchEl.value; page = 1; load(); }, 350); });
      statusEl.addEventListener('change', () => { statusFilter = statusEl.value; page = 1; load(); });
      content.querySelector('#p-create-user')!.addEventListener('click', openCreateUserModal);
      load();
    }

    function load() {
      const tbl = content.querySelector('#p-table') as HTMLDivElement;
      const pag = content.querySelector('#p-pag') as HTMLDivElement;
      tbl.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';
      api.get<Paginated<PlayerRow>>(`/admin/users?page=${page}&search=${encodeURIComponent(search)}&status=${statusFilter}`)
        .then((data) => {
          if (disposed) return;
          if (data.items.length === 0) { tbl.innerHTML = '<div class="py-6 text-center text-text-dim">No users found</div>'; pag.innerHTML = ''; return; }
          tbl.innerHTML = `<table class="w-full text-left"><thead><tr>
            <th class="${TH}">Username</th><th class="${TH}">Email</th><th class="${TH}">Role</th>
            <th class="${TH}">Balance</th><th class="${TH}">Level</th><th class="${TH}">KYC</th>
            <th class="${TH}">Email ✓</th><th class="${TH}">Status</th><th class="${TH}">Actions</th>
          </tr></thead><tbody>${data.items.map((u) => `<tr>
            <td class="${TD}">${esc(u.username ?? '—')}</td>
            <td class="${TD}">${esc(u.email)}</td>
            <td class="${TD}">${esc(u.role)}</td>
            <td class="${TD} tabular-nums text-gold">${u.softBalance.toLocaleString('en-US')}</td>
            <td class="${TD}">Lv ${u.level}</td>
            <td class="${TD}">${badge(u.kycStatus)}</td>
            <td class="${TD}">${u.emailVerified ? '<span class="text-success">✓</span>' : '<span class="text-error">✗</span>'}</td>
            <td class="${TD}">${u.banned ? '<span class="text-error">Banned</span>' : '<span class="text-success">Active</span>'}</td>
            <td class="${TD}"><div class="flex gap-1.5">
              ${u.banned ? `<button data-unban="${u.id}" class="${BTN_OUTLINE}">Unban</button>` : `<button data-ban="${u.id}" class="${BTN_DANGER}">Ban</button>`}
              ${!u.emailVerified ? `<button data-verify-email="${u.id}" class="${BTN_OUTLINE}">Verify Email</button>` : ''}
              <button data-tx="${esc(u.email)}" class="${BTN_OUTLINE}">Transactions</button>
              <button data-credit="${u.id}" class="${BTN}">Add Credits</button>
            </div></td>
          </tr>`).join('')}</tbody></table>`;
          const totalPages = Math.ceil(data.total / data.pageSize);
          pag.innerHTML = `<button data-prev class="${BTN_OUTLINE}" ${page <= 1 ? 'disabled' : ''}>&larr; Prev</button>
            <span class="font-body text-xs text-text-dim">Page ${page} of ${totalPages}</span>
            <button data-next class="${BTN_OUTLINE}" ${page >= totalPages ? 'disabled' : ''}>Next &rarr;</button>`;
          tbl.addEventListener('click', onAction);
          pag.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('[data-prev]') && page > 1) { page--; load(); }
            if ((e.target as HTMLElement).closest('[data-next]') && page < totalPages) { page++; load(); }
          });
        }).catch(() => { tbl.innerHTML = '<div class="py-6 text-center text-error">Failed to load</div>'; });
    }

    function onAction(e: Event) {
      const t = e.target as HTMLElement;
      const banBtn = t.closest('[data-ban]') as HTMLElement | null;
      const unbanBtn = t.closest('[data-unban]') as HTMLElement | null;
      const creditBtn = t.closest('[data-credit]') as HTMLElement | null;
      const verifyEmailBtn = t.closest('[data-verify-email]') as HTMLElement | null;
      const txBtn = t.closest('[data-tx]') as HTMLElement | null;
      if (txBtn) {
        // The transaction list is filtered by the email bound to the user, so
        // the jump is just a pre-filled search on the Transactions tab.
        txPrefillSearch = txBtn.dataset.tx!;
        switchTab('Transactions');
        return;
      }
      if (banBtn) {
        const id = banBtn.dataset.ban!;
        const { body, close } = adminModal(root, 'Confirm Ban', `
          <p class="mb-4 font-body text-sm text-text-dim">This user will be logged out immediately.</p>
          <div class="flex justify-end gap-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN_DANGER}">Ban</button></div>`);
        body.querySelector('[data-confirm]')!.addEventListener('click', () => {
          (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
          api.post(`/admin/users/${id}/ban`, {}).then(() => { showToast('User banned'); close(); load(); }).catch(() => showToast('Failed', 'err'));
        });
      }
      if (unbanBtn) {
        const id = unbanBtn.dataset.unban!;
        (unbanBtn as HTMLButtonElement).disabled = true;
        api.post(`/admin/users/${id}/unban`, {}).then(() => { showToast('User unbanned'); load(); }).catch(() => showToast('Failed', 'err'));
      }
      if (creditBtn) {
        const id = creditBtn.dataset.credit!;
        const { body, close } = adminModal(root, 'Add Credits', `<div class="space-y-3">
          <div><label class="${STAT_LABEL}">AMOUNT</label><input data-amt type="number" min="1" value="100" class="${INPUT} mt-1" /></div>
          <div><label class="${STAT_LABEL}">CURRENCY</label><select data-cur class="${INPUT} mt-1"><option value="SOFT">Cash</option><option value="PREMIUM">Gold Bars</option></select></div>
          <div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN}">Add</button></div>
        </div>`);
        body.querySelector('[data-confirm]')!.addEventListener('click', () => {
          const amt = parseInt((body.querySelector('[data-amt]') as HTMLInputElement).value, 10);
          const cur = (body.querySelector('[data-cur]') as HTMLSelectElement).value;
          if (!amt || amt <= 0) { showToast('Enter a valid amount', 'err'); return; }
          (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
          api.post(`/admin/users/${id}/add-credits`, { amount: amt, currency: cur })
            .then(() => { showToast('Credits added'); close(); load(); }).catch(() => showToast('Failed', 'err'));
        });
      }
      if (verifyEmailBtn) {
        const id = verifyEmailBtn.dataset.verifyEmail!;
        (verifyEmailBtn as HTMLButtonElement).disabled = true;
        api.post(`/admin/users/${id}/verify-email`, {}).then(() => { showToast('Email verified'); load(); }).catch(() => showToast('Failed', 'err'));
      }
    }

    function openCreateUserModal() {
      const { body, close } = adminModal(root, 'Create User', `<div class="space-y-3">
        <div><label class="${STAT_LABEL}">EMAIL *</label><input data-email type="email" required class="${INPUT} mt-1" placeholder="user@example.com" /></div>
        <div><label class="${STAT_LABEL}">USERNAME</label><input data-username type="text" class="${INPUT} mt-1" placeholder="Optional" /></div>
        <div><label class="${STAT_LABEL}">PASSWORD *</label><input data-password type="text" required minlength="6" class="${INPUT} mt-1" placeholder="Min 6 characters" /></div>
        <div><label class="${STAT_LABEL}">ROLE</label><select data-role class="${INPUT} mt-1"><option value="PLAYER">Player</option><option value="ADMIN">Admin</option></select></div>
        <div><label class="${STAT_LABEL}">KYC STATUS</label><select data-kyc class="${INPUT} mt-1"><option value="NONE">None</option><option value="PENDING">Pending</option><option value="APPROVED">Approved</option><option value="REJECTED">Rejected</option></select></div>
        <div class="flex items-center gap-2"><input data-verified type="checkbox" checked id="cu-verified" class="accent-gold" /><label for="cu-verified" class="${STAT_LABEL}">Email Verified</label></div>
        <div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN}">Create User</button></div>
      </div>`, { dismissOnBackdrop: false });

      body.querySelector('[data-confirm]')!.addEventListener('click', () => {
        const email = (body.querySelector('[data-email]') as HTMLInputElement).value.trim();
        const username = (body.querySelector('[data-username]') as HTMLInputElement).value.trim() || undefined;
        const password = (body.querySelector('[data-password]') as HTMLInputElement).value;
        const role = (body.querySelector('[data-role]') as HTMLSelectElement).value;
        const kycStatus = (body.querySelector('[data-kyc]') as HTMLSelectElement).value;
        const emailVerified = (body.querySelector('[data-verified]') as HTMLInputElement).checked;

        if (!email) { showToast('Email is required', 'err'); return; }
        if (!password || password.length < 6) { showToast('Password must be at least 6 characters', 'err'); return; }

        (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
        api.post('/admin/users/create', { email, username, password, role, emailVerified, kycStatus })
          .then(() => { showToast('User created'); close(); load(); })
          .catch((err: any) => {
            (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = false;
            const msg = err?.response?.data?.message || err?.message || 'Failed to create user';
            showToast(msg, 'err');
          });
      });
    }

    render();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TRANSACTIONS
  //
  // Every payment row, searchable by the email (or username) bound to the
  // paying user — the way support looks a customer up when all they have is
  // the address the receipt was sent to.
  // ═══════════════════════════════════════════════════════════════════════════

  function renderTransactions() {
    let page = 1, search = txPrefillSearch, statusFilter = '';
    txPrefillSearch = '';

    function render() {
      content.innerHTML = `
        <div class="mb-4 flex flex-wrap items-center gap-3">
          <input id="tx-search" type="text" placeholder="Search by email or username..." value="${esc(search)}" class="${INPUT} max-w-xs" />
          <select id="tx-status" class="${INPUT_SM} w-36">
            <option value="" ${statusFilter === '' ? 'selected' : ''}>All statuses</option>
            <option value="completed" ${statusFilter === 'completed' ? 'selected' : ''}>Completed</option>
            <option value="pending" ${statusFilter === 'pending' ? 'selected' : ''}>Pending</option>
            <option value="failed" ${statusFilter === 'failed' ? 'selected' : ''}>Failed</option>
            <option value="refunded" ${statusFilter === 'refunded' ? 'selected' : ''}>Refunded</option>
          </select>
          <button id="tx-clear" class="${BTN_OUTLINE}" ${search || statusFilter ? '' : 'disabled'}>Clear</button>
        </div>
        <div id="tx-table" class="overflow-x-auto"></div>
        <div id="tx-pag" class="mt-3 flex items-center gap-3"></div>`;

      const searchEl = content.querySelector('#tx-search') as HTMLInputElement;
      const statusEl = content.querySelector('#tx-status') as HTMLSelectElement;
      let debounce: ReturnType<typeof setTimeout>;
      searchEl.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => { search = searchEl.value.trim(); page = 1; load(); }, 350);
      });
      statusEl.addEventListener('change', () => { statusFilter = statusEl.value; page = 1; load(); });
      content.querySelector('#tx-clear')!.addEventListener('click', () => {
        search = ''; statusFilter = ''; page = 1; render();
      });
      load();
    }

    function load() {
      const tbl = content.querySelector('#tx-table') as HTMLDivElement;
      const pag = content.querySelector('#tx-pag') as HTMLDivElement;
      tbl.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';

      let qs = `?page=${page}`;
      if (search) qs += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) qs += `&type=${encodeURIComponent(statusFilter)}`;

      api.get<Paginated<TransactionRow>>(`/admin/transactions${qs}`)
        .then((data) => {
          if (disposed) return;
          if (data.items.length === 0) {
            tbl.innerHTML = '<div class="py-6 text-center text-text-dim">No transactions found</div>';
            pag.innerHTML = '';
            return;
          }
          tbl.innerHTML = `<table class="w-full text-left"><thead><tr>
            <th class="${TH}">Date</th><th class="${TH}">Email</th><th class="${TH}">Username</th>
            <th class="${TH}">Provider</th><th class="${TH}">Provider Tx</th>
            <th class="${TH}">Amount</th><th class="${TH}">Status</th>
          </tr></thead><tbody>${data.items.map((t) => `<tr>
            <td class="${TD} whitespace-nowrap">${fmtDate(t.createdAt)}</td>
            <td class="${TD}">${esc(t.user?.email ?? '—')}</td>
            <td class="${TD}">${esc(t.user?.username ?? '—')}</td>
            <td class="${TD}">${esc(t.provider)}</td>
            <td class="${TD} font-label text-xs">${esc(t.providerTxId)}</td>
            <td class="${TD} tabular-nums text-gold">${fmtCents(t.amountCents, t.currency)}</td>
            <td class="${TD}">${badge(t.status)}</td>
          </tr>`).join('')}</tbody></table>`;

          const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));
          pag.innerHTML = `<button data-prev class="${BTN_OUTLINE}" ${page <= 1 ? 'disabled' : ''}>&larr; Prev</button>
            <span class="font-body text-xs text-text-dim">Page ${page} of ${totalPages} · ${data.total} transaction${data.total === 1 ? '' : 's'}</span>
            <button data-next class="${BTN_OUTLINE}" ${page >= totalPages ? 'disabled' : ''}>Next &rarr;</button>`;
          pag.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('[data-prev]') && page > 1) { page--; load(); }
            if ((e.target as HTMLElement).closest('[data-next]') && page < totalPages) { page++; load(); }
          });
        })
        .catch(() => { tbl.innerHTML = '<div class="py-6 text-center text-error">Failed to load</div>'; });
    }

    render();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // KYC
  // ═══════════════════════════════════════════════════════════════════════════

  function renderKyc() {
    let page = 1, statusFilter = 'PENDING';
    function render() {
      content.innerHTML = `<div class="mb-4 flex gap-2" id="kyc-f"></div><div id="kyc-t" class="overflow-x-auto"></div><div id="kyc-p" class="mt-3 flex items-center gap-3"></div>`;
      renderFilters(); load();
    }
    function renderFilters() {
      const f = content.querySelector('#kyc-f') as HTMLDivElement;
      f.innerHTML = ['PENDING','APPROVED','REJECTED','all'].map((s) =>
        `<button data-kf="${s}" class="${s === statusFilter ? SUB_ACTIVE : SUB_IDLE}">${s === 'all' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}</button>`).join('');
      f.addEventListener('click', (e) => { const b = (e.target as HTMLElement).closest('[data-kf]') as HTMLElement | null; if (b) { statusFilter = b.dataset.kf!; page = 1; renderFilters(); load(); } });
    }
    function load() {
      const tbl = content.querySelector('#kyc-t') as HTMLDivElement;
      const pag = content.querySelector('#kyc-p') as HTMLDivElement;
      tbl.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';
      const qs = statusFilter === 'all' ? '' : `&status=${statusFilter}`;
      api.get<Paginated<PlayerRow>>(`/admin/kyc/users?page=${page}${qs}`).then((data) => {
        if (disposed) return;
        if (data.items.length === 0) { tbl.innerHTML = '<div class="py-6 text-center text-text-dim">No KYC submissions</div>'; pag.innerHTML = ''; return; }
        tbl.innerHTML = `<table class="w-full text-left"><thead><tr>
          <th class="${TH}">Username</th><th class="${TH}">Email</th><th class="${TH}">Status</th><th class="${TH}">Joined</th><th class="${TH}">Actions</th>
        </tr></thead><tbody>${data.items.map((u) => `<tr>
          <td class="${TD}">${esc(u.username ?? '—')}</td><td class="${TD}">${esc(u.email)}</td>
          <td class="${TD}">${badge(u.kycStatus)}</td><td class="${TD}">${fmtDate(u.createdAt)}</td>
          <td class="${TD}"><div class="flex gap-1.5">${u.kycStatus === 'PENDING' ? `<button data-ka="${u.id}" class="${BTN}">Approve</button><button data-kr="${u.id}" class="${BTN_DANGER}">Reject</button>` : '—'}</div></td>
        </tr>`).join('')}</tbody></table>`;
        const tp = Math.ceil(data.total / data.pageSize);
        pag.innerHTML = `<button data-prev class="${BTN_OUTLINE}" ${page <= 1 ? 'disabled' : ''}>&larr;</button><span class="font-body text-xs text-text-dim">Page ${page}/${tp}</span><button data-next class="${BTN_OUTLINE}" ${page >= tp ? 'disabled' : ''}>&rarr;</button>`;
        tbl.addEventListener('click', (e) => {
          const a = (e.target as HTMLElement).closest('[data-ka]') as HTMLElement | null;
          const r = (e.target as HTMLElement).closest('[data-kr]') as HTMLElement | null;
          if (a) { (a as HTMLButtonElement).disabled = true; api.post(`/admin/kyc/users/${a.dataset.ka}/approve`, {}).then(() => { showToast('Approved'); load(); }).catch(() => showToast('Failed', 'err')); }
          if (r) {
            const { body, close } = adminModal(root, 'Reject KYC', `<div class="space-y-3"><div><label class="${STAT_LABEL}">REASON</label><textarea data-reason rows="3" class="${INPUT} mt-1"></textarea></div><div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN_DANGER}">Reject</button></div></div>`);
            body.querySelector('[data-confirm]')!.addEventListener('click', () => {
              (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
              api.post(`/admin/kyc/users/${r.dataset.kr}/reject`, { reason: (body.querySelector('[data-reason]') as HTMLTextAreaElement).value.trim() }).then(() => { showToast('Rejected'); close(); load(); }).catch(() => showToast('Failed', 'err'));
            });
          }
        });
        pag.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('[data-prev]') && page > 1) { page--; load(); }
          if ((e.target as HTMLElement).closest('[data-next]') && page < tp) { page++; load(); }
        });
      }).catch(() => { tbl.innerHTML = '<div class="py-6 text-center text-error">Failed to load</div>'; });
    }
    render();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WITHDRAWALS
  // ═══════════════════════════════════════════════════════════════════════════

  function renderWithdrawals() {
    let page = 1, statusFilter = 'PENDING';
    function render() {
      content.innerHTML = `<div class="mb-4 flex items-center gap-4"><div class="flex gap-2" id="wd-f"></div><div id="wd-total" class="ml-auto font-heading text-sm text-gold"></div></div><div id="wd-t" class="overflow-x-auto"></div><div id="wd-p" class="mt-3 flex items-center gap-3"></div>`;
      renderFilters(); load();
    }
    function renderFilters() {
      const f = content.querySelector('#wd-f') as HTMLDivElement;
      f.innerHTML = ['PENDING','APPROVED','REJECTED','all'].map((s) =>
        `<button data-wf="${s}" class="${s === statusFilter ? SUB_ACTIVE : SUB_IDLE}">${s === 'all' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}</button>`).join('');
      f.addEventListener('click', (e) => { const b = (e.target as HTMLElement).closest('[data-wf]') as HTMLElement | null; if (b) { statusFilter = b.dataset.wf!; page = 1; renderFilters(); load(); } });
    }
    function load() {
      const tbl = content.querySelector('#wd-t') as HTMLDivElement;
      const pag = content.querySelector('#wd-p') as HTMLDivElement;
      const tot = content.querySelector('#wd-total') as HTMLDivElement;
      tbl.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';
      const qs = statusFilter === 'all' ? '' : `&status=${statusFilter}`;
      api.get<Paginated<WithdrawalRow>>(`/admin/withdrawals?page=${page}${qs}`).then((data) => {
        if (disposed) return;
        if (data.items.length === 0) { tbl.innerHTML = '<div class="py-6 text-center text-text-dim">No withdrawals</div>'; pag.innerHTML = ''; tot.textContent = ''; return; }
        tot.textContent = statusFilter === 'PENDING' ? `Pending: ${fmtCents(data.items.reduce((s, w) => s + w.amountCents, 0))}` : '';
        tbl.innerHTML = `<table class="w-full text-left"><thead><tr>
          <th class="${TH}">User</th><th class="${TH}">Amount</th><th class="${TH}">Status</th><th class="${TH}">Date</th><th class="${TH}">Actions</th>
        </tr></thead><tbody>${data.items.map((w) => `<tr>
          <td class="${TD}">${esc(w.user?.username ?? w.user?.email ?? w.email)}</td>
          <td class="${TD} tabular-nums text-gold">${fmtCents(w.amountCents, w.currency)}</td>
          <td class="${TD}">${badge(w.status)}</td><td class="${TD}">${fmtDate(w.createdAt)}</td>
          <td class="${TD}"><div class="flex gap-1.5">${w.status === 'PENDING' ? `<button data-wa="${w.id}" class="${BTN}">Approve</button><button data-wr="${w.id}" class="${BTN_DANGER}">Reject</button>` : '—'}</div></td>
        </tr>`).join('')}</tbody></table>`;
        const tp = Math.ceil(data.total / data.pageSize);
        pag.innerHTML = `<button data-prev class="${BTN_OUTLINE}" ${page <= 1 ? 'disabled' : ''}>&larr;</button><span class="font-body text-xs text-text-dim">Page ${page}/${tp}</span><button data-next class="${BTN_OUTLINE}" ${page >= tp ? 'disabled' : ''}>&rarr;</button>`;
        tbl.addEventListener('click', (e) => {
          const a = (e.target as HTMLElement).closest('[data-wa]') as HTMLElement | null;
          const r = (e.target as HTMLElement).closest('[data-wr]') as HTMLElement | null;
          if (a) { (a as HTMLButtonElement).disabled = true; api.post(`/admin/withdrawals/${a.dataset.wa}/approve`, {}).then(() => { showToast('Approved'); load(); }).catch(() => showToast('Failed', 'err')); }
          if (r) {
            const { body, close } = adminModal(root, 'Reject Withdrawal', `<div class="space-y-3"><div><label class="${STAT_LABEL}">REASON</label><textarea data-reason rows="3" class="${INPUT} mt-1"></textarea></div><div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN_DANGER}">Reject</button></div></div>`);
            body.querySelector('[data-confirm]')!.addEventListener('click', () => {
              (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
              api.post(`/admin/withdrawals/${r.dataset.wr}/reject`, { reason: (body.querySelector('[data-reason]') as HTMLTextAreaElement).value.trim() }).then(() => { showToast('Rejected'); close(); load(); }).catch(() => showToast('Failed', 'err'));
            });
          }
        });
        pag.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('[data-prev]') && page > 1) { page--; load(); }
          if ((e.target as HTMLElement).closest('[data-next]') && page < tp) { page++; load(); }
        });
      }).catch(() => { tbl.innerHTML = '<div class="py-6 text-center text-error">Failed to load</div>'; });
    }
    render();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INVOICES
  // ═══════════════════════════════════════════════════════════════════════════

  // Gold bar packs (mirrors backend gold-packages.ts ITEM_CODES + GOLD_PACKAGES)
  const INV_PACKS = [
    { id: 'stack',   code: 'MAF-BARS-00500', name: 'A Stack',        bars: 500,  priceCents: 500 },
    { id: 'roll',    code: 'MAF-BARS-01000', name: 'A Roll',         bars: 1000, priceCents: 1000 },
    { id: 'satchel', code: 'MAF-BARS-02000', name: "The Boss's Cut", bars: 2000, priceCents: 2000 },
    { id: 'vault',   code: 'MAF-BARS-05000', name: 'Kingpin Vault',  bars: 5000, priceCents: 5000 },
  ];
  const INV_CENTS_PER_BAR = 1; // 1 cent per bar = 100 bars per EUR

  function renderInvoices() {
    type InvView = 'list' | 'create';
    let invView: InvView = 'list';
    let listPage = 1, listSearch = '', listDays = 0;

    // ── Create-form state ──────────────────────────────────────────────
    let cType: 'OUT' | 'IN' = 'IN';
    let cUserId = '', cUserLabel = '';
    let cPaymentMethod = 'Credit Card';
    let cFirstName = '', cLastName = '', cCode = '', cCompany = '';
    let cAmountCents = 0, cVatRate = 0;
    let cDescription = '', cItemCode = '';
    let cTransactionId = '';
    let cStatus: 'PAID' | 'PENDING' = 'PAID';
    let cSelectedPack: string | null = null;
    let withdrawalsEnabled = true;
    let showCreateUser = false;
    let createUserEmail = '', createUserUsername = '';

    // Fetch withdrawal feature flag
    api.get<Record<string, string>>('/admin/settings').then((s) => {
      withdrawalsEnabled = s['feature.withdrawals'] === 'true';
    }).catch(() => { /* keep default true */ });

    function vatCents() { return Math.round(cAmountCents * cVatRate / 100); }
    function totalCents() { return cAmountCents + vatCents(); }
    function goldFromCents(cents: number) { return INV_CENTS_PER_BAR > 0 ? Math.round(cents / INV_CENTS_PER_BAR) : 0; }

    // ── Main render ──────────────────────────────────────────────────
    function render() {
      if (invView === 'create') renderCreateForm();
      else renderList();
    }

    // ═════════════════════════════════════════════════════════════════
    // INVOICE LIST
    // ═════════════════════════════════════════════════════════════════

    function renderList() {
      content.innerHTML = `
        <div class="mb-4 flex flex-wrap items-center gap-3">
          <input id="inv-s" type="text" placeholder="Search by number or email..." value="${esc(listSearch)}" class="${INPUT} max-w-xs" />
          <select id="inv-d" class="${INPUT_SM} w-24">
            <option value="0" ${listDays === 0 ? 'selected' : ''}>All</option>
            <option value="7" ${listDays === 7 ? 'selected' : ''}>7 days</option>
            <option value="30" ${listDays === 30 ? 'selected' : ''}>30 days</option>
            <option value="90" ${listDays === 90 ? 'selected' : ''}>90 days</option>
          </select>
          <button id="inv-tpl" class="${BTN_OUTLINE} ml-auto">Download Template</button>
          <label class="${BTN_OUTLINE}">Import XLSX<input id="inv-imp" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" class="hidden" /></label>
          <button id="inv-c" class="${BTN}">+ Create Invoice</button>
        </div>
        <div id="inv-t" class="overflow-x-auto"></div>
        <div id="inv-p" class="mt-3 flex items-center gap-3"></div>`;

      let debounce: ReturnType<typeof setTimeout>;
      (content.querySelector('#inv-s') as HTMLInputElement).addEventListener('input', (e) => {
        clearTimeout(debounce);
        debounce = setTimeout(() => { listSearch = (e.target as HTMLInputElement).value; listPage = 1; loadList(); }, 350);
      });
      (content.querySelector('#inv-d') as HTMLSelectElement).addEventListener('change', (e) => {
        listDays = parseInt((e.target as HTMLSelectElement).value, 10); listPage = 1; loadList();
      });
      content.querySelector('#inv-c')!.addEventListener('click', () => { invView = 'create'; render(); });

      // Bulk import — the sheet is keyed by the user's email, so the template
      // is fillable by whoever has the billing list and not just by whoever
      // can read user IDs out of the database.
      const tplBtn = content.querySelector('#inv-tpl') as HTMLButtonElement;
      tplBtn.addEventListener('click', () => {
        tplBtn.disabled = true;
        downloadInvoiceTemplate()
          .catch((err: Error) => showToast(`Template failed — ${err.message}`, 'err'))
          .finally(() => { tplBtn.disabled = false; });
      });

      const impEl = content.querySelector('#inv-imp') as HTMLInputElement;
      impEl.addEventListener('change', () => {
        const file = impEl.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
          showToast('File too large (max 5 MB)', 'err');
          impEl.value = '';
          return;
        }
        const fd = new FormData();
        fd.append('file', file);
        showToast('Importing…');
        api.post<{ imported: number }>('/admin/invoices/batch', fd)
          .then((res) => { showToast(`Imported ${res.imported} invoice${res.imported === 1 ? '' : 's'}`); listPage = 1; loadList(); })
          .catch((err: any) => showToast(importError(err), 'err'))
          .finally(() => { impEl.value = ''; });
      });

      loadList();
    }

    /** Flatten the all-or-nothing validation payload into one toast line. */
    function importError(err: any): string {
      const data = err?.response?.data;
      if (data?.totalErrors) return `Improper import file — ${data.totalErrors} errors`;
      if (Array.isArray(data?.errors) && data.errors.length) {
        const head = data.errors.slice(0, 3).join('; ');
        return data.errors.length > 3 ? `Row ${head} (+${data.errors.length - 3} more)` : `Row ${head}`;
      }
      return data?.message || err?.message || 'Import failed';
    }

    function loadList() {
      const tbl = content.querySelector('#inv-t') as HTMLDivElement;
      const pag = content.querySelector('#inv-p') as HTMLDivElement;
      tbl.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';
      let qs = `?page=${listPage}`;
      if (listSearch) qs += `&search=${encodeURIComponent(listSearch)}`;
      if (listDays) qs += `&days=${listDays}`;

      api.get<Paginated<InvoiceRow> & { pages: number; revenueTotal: number }>(`/admin/invoices${qs}`)
        .then((data) => {
          if (disposed) return;
          if (data.items.length === 0) {
            tbl.innerHTML = '<div class="py-6 text-center text-text-dim">No invoices</div>';
            pag.innerHTML = '';
            return;
          }
          // IN = money in / deposit, OUT = payout — matches the PDF, the
          // invoice email, and the other two forks.
          const typeBadge = (t: string) => t === 'IN'
            ? `<span class="inline-block rounded-sm px-2 py-0.5 font-label text-[10px] tracking-wider bg-success/15 text-success">DEPOSIT</span>`
            : `<span class="inline-block rounded-sm px-2 py-0.5 font-label text-[10px] tracking-wider bg-gold/15 text-gold">WITHDRAWAL</span>`;
          tbl.innerHTML = `<table class="w-full text-left"><thead><tr>
            <th class="${TH}">Number</th><th class="${TH}">Type</th><th class="${TH}">User</th>
            <th class="${TH}">Amount</th><th class="${TH}">Status</th><th class="${TH}">Date</th><th class="${TH}">PDF</th>
          </tr></thead><tbody>${data.items.map((inv) => `<tr>
            <td class="${TD} font-label text-xs">${esc(inv.number)}</td>
            <td class="${TD}">${typeBadge(inv.type)}</td>
            <td class="${TD}">${esc(inv.user?.username ?? inv.user?.email ?? '—')}</td>
            <td class="${TD} tabular-nums text-gold">${fmtCents(inv.totalCents, inv.currency)}</td>
            <td class="${TD}">${badge(inv.status)}</td>
            <td class="${TD}">${fmtDate(inv.createdAt)}</td>
            <td class="${TD}"><button data-pdf="${esc(inv.number)}" class="text-gold underline hover:brightness-110">PDF</button></td>
          </tr>`).join('')}</tbody></table>`;
          const tp = data.pages ?? Math.ceil(data.total / data.pageSize);
          pag.innerHTML = `
            <button data-prev class="${BTN_OUTLINE}" ${listPage <= 1 ? 'disabled' : ''}>&larr; Prev</button>
            <span class="font-body text-xs text-text-dim">Page ${listPage} of ${tp}</span>
            <button data-next class="${BTN_OUTLINE}" ${listPage >= tp ? 'disabled' : ''}>Next &rarr;</button>`;
          pag.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('[data-prev]') && listPage > 1) { listPage--; loadList(); }
            if ((e.target as HTMLElement).closest('[data-next]') && listPage < tp) { listPage++; loadList(); }
          });
          // PDFs are fetched, not linked: the API can live on its own origin
          // (runtime `API_URL`), so a root-relative href would hit the static
          // frontend instead. See `file-download.ts`.
          tbl.addEventListener('click', (e) => {
            const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-pdf]');
            if (!btn) return;
            const number = btn.dataset.pdf!;
            btn.textContent = '…';
            downloadInvoiceAsAdmin(number)
              .catch((err: Error) => showToast(`PDF failed — ${err.message}`, 'err'))
              .finally(() => { btn.textContent = 'PDF'; });
          });
        })
        .catch(() => { tbl.innerHTML = '<div class="py-6 text-center text-error">Failed to load</div>'; });
    }

    // ═════════════════════════════════════════════════════════════════
    // INVOICE CREATE FORM
    // ═════════════════════════════════════════════════════════════════

    function renderCreateForm() {
      const exclVat = (cAmountCents / 100).toFixed(2);
      const inclVat = (totalCents() / 100).toFixed(2);
      const goldCount = goldFromCents(cAmountCents);

      content.innerHTML = `
      <div class="mb-4 flex items-center gap-3">
        <button id="inv-back" class="${BTN_OUTLINE}">&larr; Back to List</button>
        <h2 class="${SECTION_TITLE} !mb-0">Create Invoice</h2>
      </div>
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div class="space-y-5">

          <!-- Type toggle -->
          <section>
            <label class="${STAT_LABEL}">TYPE</label>
            <div class="mt-1.5 flex gap-2">
              <button data-inv-type="IN" class="${cType === 'IN' ? SUB_ACTIVE : SUB_IDLE}">DEPOSIT</button>
              <button data-inv-type="OUT" class="${cType === 'OUT' ? SUB_ACTIVE : SUB_IDLE}" ${!withdrawalsEnabled ? 'disabled title="Withdrawals are disabled in settings"' : ''} ${!withdrawalsEnabled ? 'style="opacity:0.4;cursor:not-allowed"' : ''}>WITHDRAWAL</button>
            </div>
          </section>

          <!-- User search -->
          <section>
            <label class="${STAT_LABEL}">USER</label>
            <div class="relative mt-1.5">
              <input id="inv-user-q" type="text" placeholder="Search by email or username..." value="${esc(cUserLabel)}" class="${INPUT}" autocomplete="off" ${cUserId ? 'disabled' : ''} />
              <div id="inv-user-dd" class="absolute left-0 right-0 top-full z-50 mt-1 hidden max-h-48 overflow-y-auto rounded border border-gold/30 bg-surface shadow-lg"></div>
            </div>
            ${cUserId ? `<div class="mt-1 flex items-center gap-2 font-body text-xs text-text-dim">Selected: <span class="text-gold">${esc(cUserLabel)}</span> <span class="text-text-dim/50">(${esc(cUserId)})</span> <button id="inv-user-clear" type="button" class="${BTN_OUTLINE} !py-0.5 !px-2 !text-[10px]">Clear</button></div>` : `<div class="mt-2"><button id="inv-toggle-create-user" type="button" class="${BTN_OUTLINE} !text-[10px]">${showCreateUser ? 'Cancel' : "Can't find user? Create one"}</button></div>`}
            ${!cUserId && showCreateUser ? `
            <div class="mt-2 rounded border border-gold/20 bg-surface p-3 space-y-2">
              <div class="font-label text-[10px] tracking-wider text-text-dim uppercase">Create New User</div>
              <div class="grid grid-cols-2 gap-2">
                <div><label class="font-body text-xs text-text-dim">Email (required)</label><input id="inv-cu-email" type="email" value="${esc(createUserEmail)}" class="${INPUT} mt-0.5" placeholder="user@example.com" /></div>
                <div><label class="font-body text-xs text-text-dim">Username (optional)</label><input id="inv-cu-username" type="text" value="${esc(createUserUsername)}" class="${INPUT} mt-0.5" placeholder="Auto from email" /></div>
              </div>
              <div class="flex justify-end"><button id="inv-cu-submit" type="button" class="${BTN}">Create User</button></div>
            </div>` : ''}
          </section>

          <!-- Payment method -->
          <section>
            <label class="${STAT_LABEL}">PAYMENT METHOD</label>
            <select id="inv-pm" class="${INPUT} mt-1.5">
              ${['Credit Card', 'Credit Card (Visa)', 'Credit Card (MasterCard)', 'Card Payout', 'Card Payout (Visa)', 'Card Payout (MasterCard)']
                .map((m) => `<option value="${m}" ${cPaymentMethod === m ? 'selected' : ''}>${m}</option>`).join('')}
            </select>
          </section>

          <!-- Recipient fields -->
          <section>
            <label class="${STAT_LABEL}">RECIPIENT</label>
            <div class="mt-1.5 grid grid-cols-2 gap-3">
              <div><label class="font-body text-xs text-text-dim">First Name</label><input id="inv-fn" type="text" value="${esc(cFirstName)}" class="${INPUT} mt-0.5" /></div>
              <div><label class="font-body text-xs text-text-dim">Last Name</label><input id="inv-ln" type="text" value="${esc(cLastName)}" class="${INPUT} mt-0.5" /></div>
              <div><label class="font-body text-xs text-text-dim">Personal / Reg Code</label><input id="inv-code" type="text" value="${esc(cCode)}" class="${INPUT} mt-0.5" /></div>
              <div><label class="font-body text-xs text-text-dim">Company</label><input id="inv-comp" type="text" value="${esc(cCompany)}" class="${INPUT} mt-0.5" /></div>
            </div>
          </section>

          <!-- Pack selector -->
          <section>
            <label class="${STAT_LABEL}">GOLD BAR PACK</label>
            <div class="mt-1.5 flex flex-wrap gap-2">
              ${INV_PACKS.map((p) => `<button data-inv-pack="${p.id}" class="${cSelectedPack === p.id ? SUB_ACTIVE : SUB_IDLE}">
                ${p.bars.toLocaleString('en-US')} bars &mdash; ${fmtCents(p.priceCents)}
              </button>`).join('')}
              <button data-inv-pack="custom" class="${cSelectedPack === 'custom' ? SUB_ACTIVE : SUB_IDLE}">CUSTOM</button>
            </div>
          </section>

          <!-- Amount inputs -->
          <section>
            <label class="${STAT_LABEL}">AMOUNT</label>
            <div class="mt-1.5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <label class="font-body text-xs text-text-dim">Excl. VAT (EUR)</label>
                <input id="inv-excl" type="number" step="0.01" min="0" value="${exclVat}" class="${INPUT} mt-0.5" />
              </div>
              <div>
                <label class="font-body text-xs text-text-dim">Incl. VAT (EUR)</label>
                <input id="inv-incl" type="number" step="0.01" min="0" value="${inclVat}" class="${INPUT} mt-0.5" />
              </div>
              <div>
                <label class="font-body text-xs text-text-dim">VAT Rate (%)</label>
                <select id="inv-vat" class="${INPUT} mt-0.5">
                  ${[0, 22, 20].map((r) => `<option value="${r}" ${cVatRate === r ? 'selected' : ''}>${r}%</option>`).join('')}
                </select>
              </div>
              <div>
                <label class="font-body text-xs text-text-dim">Gold Bars</label>
                <input id="inv-gold" type="number" min="0" value="${goldCount}" class="${INPUT} mt-0.5" readonly />
              </div>
            </div>
          </section>

          <!-- Description + Item Code -->
          <section>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label class="${STAT_LABEL}">DESCRIPTION</label><input id="inv-desc" type="text" value="${esc(cDescription)}" class="${INPUT} mt-1.5" /></div>
              <div><label class="${STAT_LABEL}">ITEM CODE</label><input id="inv-icode" type="text" value="${esc(cItemCode)}" class="${INPUT} mt-1.5" /></div>
            </div>
          </section>

          <!-- Transaction ID + Status -->
          <section>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label class="${STAT_LABEL}">TRANSACTION ID</label><input id="inv-txn" type="text" value="${esc(cTransactionId)}" placeholder="Optional payment reference" class="${INPUT} mt-1.5" /></div>
              <div><label class="${STAT_LABEL}">STATUS</label>
                <select id="inv-status" class="${INPUT} mt-1.5">
                  <option value="PAID" ${cStatus === 'PAID' ? 'selected' : ''}>PAID</option>
                  <option value="PENDING" ${cStatus === 'PENDING' ? 'selected' : ''}>NOT PAID</option>
                </select>
              </div>
            </div>
          </section>

          <!-- Generate button -->
          <div class="flex justify-end pt-2">
            <button id="inv-gen" class="${BTN}" ${!cUserId ? 'disabled' : ''}>Generate Invoice</button>
          </div>
        </div>

        <!-- Totals preview sidebar -->
        <div class="rounded border border-gold/20 bg-surface p-4 self-start">
          <h3 class="${SECTION_TITLE} text-base">Preview</h3>
          <div class="space-y-2 font-body text-sm">
            <div class="flex justify-between"><span class="text-text-dim">Type</span><span class="text-text">${cType === 'IN' ? 'DEPOSIT' : 'WITHDRAWAL'}</span></div>
            <div class="flex justify-between"><span class="text-text-dim">Currency</span><span class="text-text">EUR</span></div>
            <div class="flex justify-between"><span class="text-text-dim">Payment</span><span class="text-text">${esc(cPaymentMethod)}</span></div>
            ${cSelectedPack && cSelectedPack !== 'custom' ? `<div class="flex justify-between"><span class="text-text-dim">Pack</span><span class="text-gold">${esc(INV_PACKS.find((p) => p.id === cSelectedPack)?.name ?? '')}</span></div>` : ''}
            <hr class="border-gold/10" />
            <div class="flex justify-between"><span class="text-text-dim">Subtotal</span><span class="text-text tabular-nums">${fmtCents(cAmountCents)}</span></div>
            <div class="flex justify-between"><span class="text-text-dim">VAT (${cVatRate}%)</span><span class="text-text tabular-nums">${fmtCents(vatCents())}</span></div>
            <div class="flex justify-between font-heading text-base"><span class="text-text">Total</span><span class="text-gold tabular-nums">${fmtCents(totalCents())}</span></div>
            <hr class="border-gold/10" />
            <div class="flex justify-between"><span class="text-text-dim">Gold Bars</span><span class="text-gold tabular-nums">${goldCount.toLocaleString('en-US')}</span></div>
            ${cItemCode ? `<div class="flex justify-between"><span class="text-text-dim">Item Code</span><span class="text-text font-label text-xs">${esc(cItemCode)}</span></div>` : ''}
            <div class="flex justify-between"><span class="text-text-dim">Status</span><span class="${cStatus === 'PAID' ? 'text-success' : 'text-error'} font-label text-xs">${cStatus === 'PAID' ? 'PAID' : 'NOT PAID'}</span></div>
            ${cTransactionId ? `<div class="flex justify-between"><span class="text-text-dim">Txn ID</span><span class="text-text font-label text-xs">${esc(cTransactionId)}</span></div>` : ''}
          </div>
        </div>
      </div>`;

      // ── Wire up events ──────────────────────────────────────────

      // Back button
      content.querySelector('#inv-back')!.addEventListener('click', () => { invView = 'list'; render(); });

      // Type toggle
      content.querySelectorAll<HTMLButtonElement>('[data-inv-type]').forEach((btn) => {
        btn.addEventListener('click', () => { if (btn.disabled) return; cType = btn.dataset.invType as 'OUT' | 'IN'; renderCreateForm(); });
      });

      // Clear user button
      const clearBtn = content.querySelector('#inv-user-clear');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          cUserId = ''; cUserLabel = '';
          showCreateUser = false; createUserEmail = ''; createUserUsername = '';
          renderCreateForm();
        });
      }

      // Toggle create user section
      const toggleCreateBtn = content.querySelector('#inv-toggle-create-user');
      if (toggleCreateBtn) {
        toggleCreateBtn.addEventListener('click', () => { showCreateUser = !showCreateUser; renderCreateForm(); });
      }

      // Create user form
      const cuEmailInput = content.querySelector('#inv-cu-email') as HTMLInputElement | null;
      const cuUsernameInput = content.querySelector('#inv-cu-username') as HTMLInputElement | null;
      const cuSubmitBtn = content.querySelector('#inv-cu-submit') as HTMLButtonElement | null;
      if (cuEmailInput) cuEmailInput.addEventListener('input', () => { createUserEmail = cuEmailInput.value; });
      if (cuUsernameInput) cuUsernameInput.addEventListener('input', () => { createUserUsername = cuUsernameInput.value; });
      if (cuSubmitBtn) {
        cuSubmitBtn.addEventListener('click', () => {
          const email = createUserEmail.trim();
          if (!email) { showToast('Email is required', 'err'); return; }
          cuSubmitBtn.disabled = true;
          cuSubmitBtn.textContent = 'Creating...';
          api.post<{ id: string; username: string | null; email: string; created: boolean }>('/admin/users/create-for-invoice', {
            email,
            username: createUserUsername.trim() || undefined,
          })
            .then((res) => {
              cUserId = res.id;
              cUserLabel = res.username ?? res.email;
              showCreateUser = false; createUserEmail = ''; createUserUsername = '';
              showToast(res.created ? 'User created' : 'Existing user selected');
              renderCreateForm();
            })
            .catch(() => {
              showToast('Failed to create user', 'err');
              cuSubmitBtn.disabled = false;
              cuSubmitBtn.textContent = 'Create User';
            });
        });
      }

      // User search
      const userInput = content.querySelector('#inv-user-q') as HTMLInputElement;
      const userDD = content.querySelector('#inv-user-dd') as HTMLDivElement;
      let userDebounce: ReturnType<typeof setTimeout>;
      userInput.addEventListener('input', () => {
        clearTimeout(userDebounce);
        const q = userInput.value.trim();
        if (q.length < 2) { userDD.classList.add('hidden'); return; }
        userDebounce = setTimeout(() => {
          api.get<Paginated<PlayerRow>>(`/admin/users?search=${encodeURIComponent(q)}&limit=10`)
            .then((data) => {
              if (disposed) return;
              if (data.items.length === 0) {
                userDD.innerHTML = '<div class="px-3 py-2 text-xs text-text-dim">No users found</div>';
              } else {
                userDD.innerHTML = data.items.map((u) =>
                  `<button type="button" data-uid="${esc(u.id)}" data-ulabel="${esc(u.username ?? u.email)}" class="block w-full px-3 py-2 text-left font-body text-sm text-text hover:bg-gold/10 cursor-pointer">
                    <span class="text-text">${esc(u.username ?? '—')}</span>
                    <span class="ml-2 text-xs text-text-dim">${esc(u.email)}</span>
                  </button>`
                ).join('');
              }
              userDD.classList.remove('hidden');
            })
            .catch(() => { userDD.classList.add('hidden'); });
        }, 300);
      });
      userDD.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest('[data-uid]') as HTMLElement | null;
        if (!btn) return;
        cUserId = btn.dataset.uid!;
        cUserLabel = btn.dataset.ulabel!;
        userDD.classList.add('hidden');
        renderCreateForm();
      });
      userInput.addEventListener('blur', () => { setTimeout(() => userDD.classList.add('hidden'), 200); });

      // Payment method
      (content.querySelector('#inv-pm') as HTMLSelectElement).addEventListener('change', (e) => {
        cPaymentMethod = (e.target as HTMLSelectElement).value;
        renderCreateForm();
      });

      // Recipient fields
      const bindField = (sel: string, setter: (v: string) => void) => {
        (content.querySelector(sel) as HTMLInputElement).addEventListener('input', (e) => { setter((e.target as HTMLInputElement).value); });
      };
      bindField('#inv-fn', (v) => { cFirstName = v; });
      bindField('#inv-ln', (v) => { cLastName = v; });
      bindField('#inv-code', (v) => { cCode = v; });
      bindField('#inv-comp', (v) => { cCompany = v; });

      // Pack selector
      content.querySelectorAll<HTMLButtonElement>('[data-inv-pack]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const packId = btn.dataset.invPack!;
          cSelectedPack = packId;
          if (packId !== 'custom') {
            const pack = INV_PACKS.find((p) => p.id === packId)!;
            cAmountCents = pack.priceCents;
            cDescription = `${pack.name} — ${pack.bars.toLocaleString('en-US')} gold bars`;
            cItemCode = pack.code;
          }
          renderCreateForm();
        });
      });

      // Amount inputs — excl VAT drives
      const exclInput = content.querySelector('#inv-excl') as HTMLInputElement;
      const inclInput = content.querySelector('#inv-incl') as HTMLInputElement;
      const vatSelect = content.querySelector('#inv-vat') as HTMLSelectElement;

      exclInput.addEventListener('input', () => {
        const v = parseFloat(exclInput.value) || 0;
        cAmountCents = Math.round(v * 100);
        cSelectedPack = 'custom';
        renderCreateForm();
      });
      inclInput.addEventListener('input', () => {
        const v = parseFloat(inclInput.value) || 0;
        const inclCents = Math.round(v * 100);
        cAmountCents = Math.round(inclCents / (1 + cVatRate / 100));
        cSelectedPack = 'custom';
        renderCreateForm();
      });
      vatSelect.addEventListener('change', () => {
        cVatRate = parseInt(vatSelect.value, 10);
        renderCreateForm();
      });

      // Description + item code
      (content.querySelector('#inv-desc') as HTMLInputElement).addEventListener('input', (e) => { cDescription = (e.target as HTMLInputElement).value; });
      (content.querySelector('#inv-icode') as HTMLInputElement).addEventListener('input', (e) => { cItemCode = (e.target as HTMLInputElement).value; });

      // Transaction ID + Status
      (content.querySelector('#inv-txn') as HTMLInputElement).addEventListener('input', (e) => { cTransactionId = (e.target as HTMLInputElement).value; });
      (content.querySelector('#inv-status') as HTMLSelectElement).addEventListener('change', (e) => { cStatus = (e.target as HTMLSelectElement).value as 'PAID' | 'PENDING'; renderCreateForm(); });

      // Generate
      content.querySelector('#inv-gen')!.addEventListener('click', () => {
        if (!cUserId) { showToast('Select a user first', 'err'); return; }
        if (cAmountCents <= 0) { showToast('Amount must be positive', 'err'); return; }
        if (!cDescription.trim()) { showToast('Description required', 'err'); return; }

        const genBtn = content.querySelector('#inv-gen') as HTMLButtonElement;
        genBtn.disabled = true;
        genBtn.textContent = 'Generating...';

        api.post('/admin/invoices/generate', {
          userId: cUserId,
          type: cType,
          amountCents: cAmountCents,
          vatRate: cVatRate,
          currency: 'EUR',
          description: cDescription,
          recipientName: cFirstName || undefined,
          recipientSurname: cLastName || undefined,
          recipientCompany: cCompany || undefined,
          recipientCode: cCode || undefined,
          paymentMethod: cPaymentMethod,
          itemCode: cItemCode || undefined,
          transactionId: cTransactionId || undefined,
          status: cStatus,
        })
          .then(() => {
            showToast('Invoice created');
            // Reset form
            cUserId = ''; cUserLabel = ''; cFirstName = ''; cLastName = '';
            cCode = ''; cCompany = ''; cAmountCents = 0; cDescription = '';
            cItemCode = ''; cSelectedPack = null; cTransactionId = '';
            cStatus = 'PAID'; showCreateUser = false; createUserEmail = ''; createUserUsername = '';
            invView = 'list'; listPage = 1;
            render();
          })
          .catch(() => {
            showToast('Failed to create invoice', 'err');
            genBtn.disabled = false;
            genBtn.textContent = 'Generate Invoice';
          });
      });
    }

    render();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CMS
  // ═══════════════════════════════════════════════════════════════════════════

  function renderCms() {
    type CmsTab = 'faq' | 'legal' | 'social' | 'news';
    let cmsTab: CmsTab = 'faq';
    function render() {
      content.innerHTML = `<div class="mb-4 flex gap-2" id="cms-tabs"></div><div id="cms-c"></div>`;
      renderTabs(); renderContent();
    }
    function renderTabs() {
      const el = content.querySelector('#cms-tabs') as HTMLDivElement;
      const opts: Array<{ id: CmsTab; label: string }> = [{ id: 'faq', label: 'FAQ' }, { id: 'legal', label: 'Legal' }, { id: 'social', label: 'Social Links' }, { id: 'news', label: 'News' }];
      el.innerHTML = opts.map((o) => `<button data-ct="${o.id}" class="${o.id === cmsTab ? SUB_ACTIVE : SUB_IDLE}">${o.label}</button>`).join('');
      el.addEventListener('click', (e) => { const b = (e.target as HTMLElement).closest('[data-ct]') as HTMLElement | null; if (b) { cmsTab = b.dataset.ct as CmsTab; renderTabs(); renderContent(); } });
    }
    function renderContent() { switch (cmsTab) { case 'faq': renderFaq(); break; case 'legal': renderLegal(); break; case 'social': renderSocial(); break; case 'news': renderNews(); break; } }

    function renderFaq() {
      const area = content.querySelector('#cms-c') as HTMLDivElement;
      area.innerHTML = '<div class="py-4 text-center text-text-dim">Loading...</div>';
      api.get<ListResponse<FaqRow>>('/admin/faq?limit=100').then(listItems).then((items) => {
        if (disposed) return;
        area.innerHTML = `<div class="mb-3 flex justify-end"><button data-faq-add class="${BTN}">+ Add FAQ</button></div>
        <div class="space-y-2">${items.length === 0 ? '<div class="py-4 text-center text-text-dim">No entries</div>' : items.map((f) => `
          <div class="rounded border border-gold/20 bg-surface p-3"><div class="flex items-start justify-between gap-3"><div class="flex-1">
            <div class="font-heading text-sm text-text">${esc(f.question)}</div>
            <div class="mt-1 font-body text-xs text-text-dim">${esc(f.answer).slice(0, 120)}${f.answer.length > 120 ? '...' : ''}</div>
          </div><div class="flex shrink-0 items-center gap-2">
            <span class="font-label text-[10px] ${f.active ? 'text-success' : 'text-text-dim/30'}">${f.active ? 'Active' : 'Hidden'}</span>
            <button data-faq-e="${f.id}" class="${BTN_OUTLINE}">Edit</button><button data-faq-d="${f.id}" class="${BTN_DANGER}">Del</button>
          </div></div></div>`).join('')}</div>`;
        area.addEventListener('click', (e) => {
          const t = e.target as HTMLElement;
          if (t.closest('[data-faq-add]')) openFaqForm();
          const eb = t.closest('[data-faq-e]') as HTMLElement | null; if (eb) openFaqForm(items.find((f) => f.id === eb.dataset.faqE));
          const db = t.closest('[data-faq-d]') as HTMLElement | null; if (db) api.del(`/admin/faq/${db.dataset.faqD}`).then(() => { showToast('Deleted'); renderFaq(); }).catch(() => showToast('Failed', 'err'));
        });
      }).catch(() => { area.innerHTML = '<div class="py-4 text-center text-error">Failed to load</div>'; });
    }
    function openFaqForm(ex?: FaqRow) {
      const { body, close } = adminModal(root, ex ? 'Edit FAQ' : 'Add FAQ', `<div class="space-y-3">
        <div><label class="${STAT_LABEL}">QUESTION</label><input data-q type="text" value="${esc(ex?.question ?? '')}" class="${INPUT} mt-1" /></div>
        <div><label class="${STAT_LABEL}">ANSWER</label><div data-editor class="mt-1"></div></div>
        <div class="grid grid-cols-2 gap-3"><div><label class="${STAT_LABEL}">SORT ORDER</label><input data-sort type="number" value="${ex?.sortOrder ?? 0}" class="${INPUT} mt-1" /></div>
        <div class="flex items-end gap-2 pb-0.5"><label class="flex items-center gap-2 font-body text-sm text-text"><input data-active type="checkbox" ${ex?.active !== false ? 'checked' : ''} /> Active</label></div></div>
        <div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN}">${ex ? 'Save' : 'Create'}</button></div>
      </div>`);
      const editor = mountCmsEditor(body.querySelector('[data-editor]')!, { value: ex?.answer ?? '', placeholder: 'Write the answer…' });
      body.querySelector('[data-confirm]')!.addEventListener('click', () => {
        const q = (body.querySelector('[data-q]') as HTMLInputElement).value.trim();
        const a = editor.getHtml();
        if (!q || !a) { showToast('Required', 'err'); return; }
        (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
        const d = { question: q, answer: a, sortOrder: parseInt((body.querySelector('[data-sort]') as HTMLInputElement).value, 10) || 0, active: (body.querySelector('[data-active]') as HTMLInputElement).checked };
        (ex ? api.patch(`/admin/faq/${ex.id}`, d) : api.post('/admin/faq', d)).then(() => { showToast(ex ? 'Updated' : 'Created'); close(); renderFaq(); }).catch(() => showToast('Failed', 'err'));
      });
    }

    function renderLegal() {
      const area = content.querySelector('#cms-c') as HTMLDivElement;
      area.innerHTML = '<div class="py-4 text-center text-text-dim">Loading...</div>';
      api.get<ListResponse<LegalRow>>('/admin/legal?limit=100').then(listItems).then((items) => {
        if (disposed) return;
        area.innerHTML = `<div class="mb-3 flex justify-end"><button data-lg-add class="${BTN}">+ Add Legal Page</button></div>
        <div class="space-y-2">${items.length === 0 ? '<div class="py-4 text-center text-text-dim">No pages</div>' : items.map((l) => `
          <div class="rounded border border-gold/20 bg-surface p-3"><div class="flex items-center justify-between gap-3">
            <div><span class="font-heading text-sm text-text">${esc(l.title)}</span><span class="ml-2 font-label text-xs text-text-dim">/${esc(l.slug)}</span></div>
            <div class="flex items-center gap-2"><span class="font-label text-[10px] ${l.active ? 'text-success' : 'text-text-dim/30'}">${l.active ? 'Active' : 'Hidden'}</span>
            <button data-lg-e="${l.id}" class="${BTN_OUTLINE}">Edit</button><button data-lg-d="${l.id}" class="${BTN_DANGER}">Del</button></div>
          </div></div>`).join('')}</div>`;
        area.addEventListener('click', (e) => {
          const t = e.target as HTMLElement;
          if (t.closest('[data-lg-add]')) openLegalForm();
          const eb = t.closest('[data-lg-e]') as HTMLElement | null; if (eb) openLegalForm(items.find((l) => l.id === eb.dataset.lgE));
          const db = t.closest('[data-lg-d]') as HTMLElement | null; if (db) api.del(`/admin/legal/${db.dataset.lgD}`).then(() => { showToast('Deleted'); renderLegal(); }).catch(() => showToast('Failed', 'err'));
        });
      }).catch(() => { area.innerHTML = '<div class="py-4 text-center text-error">Failed to load</div>'; });
    }
    function openLegalForm(ex?: LegalRow) {
      const { body, close } = adminModal(root, ex ? 'Edit Legal Page' : 'Add Legal Page', `<div class="space-y-3">
        <div><label class="${STAT_LABEL}">SLUG</label><input data-slug type="text" value="${esc(ex?.slug ?? '')}" class="${INPUT} mt-1" placeholder="e.g. privacy" /></div>
        <div><label class="${STAT_LABEL}">TITLE</label><input data-title type="text" value="${esc(ex?.title ?? '')}" class="${INPUT} mt-1" /></div>
        <div><label class="${STAT_LABEL}">CONTENT</label><div data-editor class="mt-1"></div></div>
        <div class="flex items-center gap-2"><label class="flex items-center gap-2 font-body text-sm text-text"><input data-active type="checkbox" ${ex?.active !== false ? 'checked' : ''} /> Active</label></div>
        <div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN}">${ex ? 'Save' : 'Create'}</button></div>
      </div>`);
      const editor = mountCmsEditor(body.querySelector('[data-editor]')!, { value: ex?.content ?? '', placeholder: 'Write legal content…' });
      body.querySelector('[data-confirm]')!.addEventListener('click', () => {
        const slug = (body.querySelector('[data-slug]') as HTMLInputElement).value.trim();
        const title = (body.querySelector('[data-title]') as HTMLInputElement).value.trim();
        const ct = editor.getHtml();
        if (!slug || !title || !ct) { showToast('Required', 'err'); return; }
        (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
        const d = { slug, title, content: ct, active: (body.querySelector('[data-active]') as HTMLInputElement).checked };
        (ex ? api.patch(`/admin/legal/${ex.id}`, d) : api.post('/admin/legal', d)).then(() => { showToast(ex ? 'Updated' : 'Created'); close(); renderLegal(); }).catch(() => showToast('Failed', 'err'));
      });
    }

    function renderSocial() {
      const area = content.querySelector('#cms-c') as HTMLDivElement;
      area.innerHTML = '<div class="py-4 text-center text-text-dim">Loading...</div>';
      api.get<ListResponse<SocialRow>>('/admin/social-links?limit=100').then(listItems).then((items) => {
        if (disposed) return;
        area.innerHTML = `<div class="mb-3 flex justify-end"><button data-sl-add class="${BTN}">+ Add Link</button></div>
        <div class="space-y-2">${items.length === 0 ? '<div class="py-4 text-center text-text-dim">No links</div>' : items.map((s) => `
          <div class="rounded border border-gold/20 bg-surface p-3"><div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2"><span class="text-lg">${esc(s.icon)}</span><span class="font-heading text-sm text-text">${esc(s.platform)}</span><span class="font-body text-xs text-text-dim">${esc(s.url)}</span></div>
            <div class="flex items-center gap-2"><span class="font-label text-[10px] ${s.active ? 'text-success' : 'text-text-dim/30'}">${s.active ? 'Active' : 'Hidden'}</span>
            <button data-sl-e="${s.id}" class="${BTN_OUTLINE}">Edit</button><button data-sl-d="${s.id}" class="${BTN_DANGER}">Del</button></div>
          </div></div>`).join('')}</div>`;
        area.addEventListener('click', (e) => {
          const t = e.target as HTMLElement;
          if (t.closest('[data-sl-add]')) openSocialForm();
          const eb = t.closest('[data-sl-e]') as HTMLElement | null; if (eb) openSocialForm(items.find((s) => s.id === eb.dataset.slE));
          const db = t.closest('[data-sl-d]') as HTMLElement | null; if (db) api.del(`/admin/social-links/${db.dataset.slD}`).then(() => { showToast('Deleted'); renderSocial(); }).catch(() => showToast('Failed', 'err'));
        });
      }).catch(() => { area.innerHTML = '<div class="py-4 text-center text-error">Failed to load</div>'; });
    }
    function openSocialForm(ex?: SocialRow) {
      const { body, close } = adminModal(root, ex ? 'Edit Social Link' : 'Add Social Link', `<div class="space-y-3">
        <div><label class="${STAT_LABEL}">PLATFORM</label><input data-platform type="text" value="${esc(ex?.platform ?? '')}" class="${INPUT} mt-1" /></div>
        <div><label class="${STAT_LABEL}">URL</label><input data-url type="url" value="${esc(ex?.url ?? '')}" class="${INPUT} mt-1" /></div>
        <div class="grid grid-cols-2 gap-3"><div><label class="${STAT_LABEL}">ICON (emoji)</label><input data-icon type="text" value="${esc(ex?.icon ?? '')}" class="${INPUT} mt-1" /></div>
        <div><label class="${STAT_LABEL}">SORT ORDER</label><input data-sort type="number" value="${ex?.sortOrder ?? 0}" class="${INPUT} mt-1" /></div></div>
        <div class="flex items-center gap-2"><label class="flex items-center gap-2 font-body text-sm text-text"><input data-active type="checkbox" ${ex?.active !== false ? 'checked' : ''} /> Active</label></div>
        <div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN}">${ex ? 'Save' : 'Create'}</button></div>
      </div>`);
      body.querySelector('[data-confirm]')!.addEventListener('click', () => {
        const platform = (body.querySelector('[data-platform]') as HTMLInputElement).value.trim();
        const url = (body.querySelector('[data-url]') as HTMLInputElement).value.trim();
        if (!platform || !url) { showToast('Required', 'err'); return; }
        (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
        const d = { platform, url, icon: (body.querySelector('[data-icon]') as HTMLInputElement).value.trim(), sortOrder: parseInt((body.querySelector('[data-sort]') as HTMLInputElement).value, 10) || 0, active: (body.querySelector('[data-active]') as HTMLInputElement).checked };
        (ex ? api.patch(`/admin/social-links/${ex.id}`, d) : api.post('/admin/social-links', d)).then(() => { showToast(ex ? 'Updated' : 'Created'); close(); renderSocial(); }).catch(() => showToast('Failed', 'err'));
      });
    }

    function renderNews() {
      const area = content.querySelector('#cms-c') as HTMLDivElement;
      area.innerHTML = '<div class="py-4 text-center text-text-dim">Loading...</div>';
      api.get<{ items: NewsRow[]; total: number }>('/admin/news').then((data) => {
        if (disposed) return;
        const items = data.items;
        area.innerHTML = `<div class="mb-3 flex justify-end"><button data-nw-add class="${BTN}">+ Add News</button></div>
        <div class="space-y-2">${items.length === 0 ? '<div class="py-4 text-center text-text-dim">No articles</div>' : items.map((n) => `
          <div class="rounded border border-gold/20 bg-surface p-3"><div class="flex items-start justify-between gap-3"><div class="flex-1">
            <div class="font-heading text-sm text-text">${esc(n.title)}</div>
            <div class="mt-0.5 flex items-center gap-2">${n.tag ? `<span class="rounded-sm bg-gold/15 px-1.5 py-0.5 font-label text-[9px] text-gold">${esc(n.tag)}</span>` : ''}<span class="font-body text-xs text-text-dim">${fmtDate(n.createdAt)}</span></div>
          </div><div class="flex shrink-0 items-center gap-2">
            <span class="font-label text-[10px] ${n.published ? 'text-success' : 'text-text-dim/30'}">${n.published ? 'Published' : 'Draft'}</span>
            <button data-nw-e="${n.id}" class="${BTN_OUTLINE}">Edit</button><button data-nw-d="${n.id}" class="${BTN_DANGER}">Del</button>
          </div></div></div>`).join('')}</div>`;
        area.addEventListener('click', (e) => {
          const t = e.target as HTMLElement;
          if (t.closest('[data-nw-add]')) openNewsForm();
          const eb = t.closest('[data-nw-e]') as HTMLElement | null; if (eb) openNewsForm(items.find((n) => n.id === eb.dataset.nwE));
          const db = t.closest('[data-nw-d]') as HTMLElement | null; if (db) api.del(`/admin/news/${db.dataset.nwD}`).then(() => { showToast('Deleted'); renderNews(); }).catch(() => showToast('Failed', 'err'));
        });
      }).catch(() => { area.innerHTML = '<div class="py-4 text-center text-error">Failed to load</div>'; });
    }
    function openNewsForm(ex?: NewsRow) {
      const { body, close } = adminModal(root, ex ? 'Edit News' : 'Add News', `<div class="space-y-3">
        <div><label class="${STAT_LABEL}">TITLE</label><input data-title type="text" value="${esc(ex?.title ?? '')}" class="${INPUT} mt-1" /></div>
        <div><label class="${STAT_LABEL}">SUMMARY</label><textarea data-summary rows="2" class="${INPUT} mt-1">${esc(ex?.summary ?? '')}</textarea></div>
        <div><label class="${STAT_LABEL}">CONTENT</label><div data-editor class="mt-1"></div></div>
        <div class="grid grid-cols-2 gap-3"><div><label class="${STAT_LABEL}">TAG</label><input data-tag type="text" value="${esc(ex?.tag ?? '')}" class="${INPUT} mt-1" /></div>
        <div class="flex items-end gap-2 pb-0.5"><label class="flex items-center gap-2 font-body text-sm text-text"><input data-pub type="checkbox" ${ex?.published ? 'checked' : ''} /> Published</label></div></div>
        <div class="flex justify-end gap-2 pt-2"><button data-modal-close class="${BTN_OUTLINE}">Cancel</button><button data-confirm class="${BTN}">${ex ? 'Save' : 'Create'}</button></div>
      </div>`);
      const editor = mountCmsEditor(body.querySelector('[data-editor]')!, { value: ex?.content ?? '', placeholder: 'Write article content…' });
      body.querySelector('[data-confirm]')!.addEventListener('click', () => {
        const title = (body.querySelector('[data-title]') as HTMLInputElement).value.trim();
        if (!title) { showToast('Title required', 'err'); return; }
        (body.querySelector('[data-confirm]') as HTMLButtonElement).disabled = true;
        const d = { title, summary: (body.querySelector('[data-summary]') as HTMLTextAreaElement).value.trim(), content: editor.getHtml(), tag: (body.querySelector('[data-tag]') as HTMLInputElement).value.trim(), published: (body.querySelector('[data-pub]') as HTMLInputElement).checked };
        (ex ? api.patch(`/admin/news/${ex.id}`, d) : api.post('/admin/news', d)).then(() => { showToast(ex ? 'Updated' : 'Created'); close(); renderNews(); }).catch(() => showToast('Failed', 'err'));
      });
    }

    render();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // META TAGS / SEO
  // ═══════════════════════════════════════════════════════════════════════════

  interface MetaResponse {
    values: Record<string, string>;
    resolved: {
      title: string; description: string; siteName: string; url: string;
      locale: string; image: string; imageAlt: string; imageWidth: string;
      imageHeight: string; twitterCard: string; icons: Record<string, string>;
    };
  }

  const ICON_SLOTS: { key: string; label: string; accept: string }[] = [
    { key: 'icon_ico', label: 'favicon.ico', accept: '.ico' },
    { key: 'icon_png_16', label: '16x16 PNG', accept: '.png' },
    { key: 'icon_png_32', label: '32x32 PNG', accept: '.png' },
    { key: 'icon_png_100', label: '100x100 PNG', accept: '.png' },
    { key: 'icon_png_180', label: '180x180 PNG', accept: '.png' },
    { key: 'icon_png_240', label: '240x240 PNG', accept: '.png' },
  ];

  function renderMeta() {
    content.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';

    function render(data: MetaResponse) {
      if (disposed) return;
      const v = (k: string) => data.values[k] ?? '';
      const r = data.resolved;

      const metaField = (label: string, key: string, placeholder?: string) =>
        `<div><label class="${STAT_LABEL}">${label.toUpperCase()}</label>
         <input data-meta="${key}" type="text" value="${esc(v(key))}" placeholder="${esc(placeholder ?? '')}" class="${INPUT} mt-1" /></div>`;

      const twitterOptions = ['summary', 'summary_large_image'].map(opt =>
        `<option value="${opt}" ${v('twitter_card') === opt ? 'selected' : ''}>${opt}</option>`
      ).join('');

      content.innerHTML = `<div class="space-y-8">
        <section><h2 class="${SECTION_TITLE}">SEO / Open Graph</h2>
          <form id="meta-form" class="space-y-3">
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              ${metaField('Title', 'title', r.title)}
              ${metaField('Site Name', 'site_name', r.siteName)}
              ${metaField('URL', 'url', r.url)}
              ${metaField('Locale', 'locale', r.locale)}
            </div>
            <div>${'<label class="' + STAT_LABEL + '">DESCRIPTION</label>'}
              <textarea data-meta="description" rows="3" placeholder="${esc(r.description)}" class="${INPUT} mt-1">${esc(v('description'))}</textarea>
            </div>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              ${metaField('Image Alt Text', 'image_alt', r.imageAlt)}
              <div><label class="${STAT_LABEL}">TWITTER CARD</label>
                <select data-meta="twitter_card" class="${INPUT} mt-1">${twitterOptions}</select>
              </div>
            </div>
            <div class="flex justify-end"><button type="submit" class="${BTN}">Save Meta</button></div>
          </form>
        </section>

        <section><h2 class="${SECTION_TITLE}">Share Image (OG / Twitter)</h2>
          <div class="flex items-start gap-6">
            <div class="shrink-0">
              ${r.image
                ? `<img src="${esc(r.image)}" alt="OG" class="h-24 w-auto rounded border border-gold/20" />`
                : `<div class="grid h-24 w-40 place-items-center rounded border border-dashed border-gold/20 text-text-dim text-xs">No image</div>`}
            </div>
            <div class="space-y-2">
              <label class="${BTN} inline-block cursor-pointer"><input id="og-upload" type="file" accept=".png,.jpg,.jpeg" class="hidden" />Upload Image</label>
              ${r.image ? `<button id="og-remove" class="${BTN_DANGER} ml-2">Remove</button>` : ''}
              <div class="font-body text-xs text-text-dim">PNG or JPEG, min 200x200${r.imageWidth ? ` — current: ${r.imageWidth}x${r.imageHeight}` : ''}</div>
            </div>
          </div>
        </section>

        <section><h2 class="${SECTION_TITLE}">Favicon / Icons</h2>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
            ${ICON_SLOTS.map(slot => {
              const url = r.icons?.[slot.key] ?? '';
              return `<div class="rounded border border-gold/20 bg-surface p-3 text-center">
                <div class="mb-2 font-label text-[10px] tracking-wider text-text-dim">${slot.label}</div>
                ${url
                  ? `<img src="${esc(url)}" alt="${slot.label}" class="mx-auto mb-2 h-8 w-8 object-contain" />`
                  : `<div class="mx-auto mb-2 grid h-8 w-8 place-items-center text-text-dim text-xs">—</div>`}
                <label class="${BTN_OUTLINE} inline-block cursor-pointer text-[10px]"><input type="file" data-icon-slot="${slot.key}" accept="${slot.accept}" class="hidden" />Upload</label>
                ${url ? `<button data-icon-remove="${slot.key}" class="mt-1 block w-full text-center font-label text-[10px] text-crimson hover:underline cursor-pointer">Remove</button>` : ''}
              </div>`;
            }).join('')}
          </div>
        </section>

        <section><h2 class="${SECTION_TITLE}">Preview</h2>
          <div class="rounded border border-gold/20 bg-surface p-4 space-y-1 font-body text-sm">
            <div><span class="text-text-dim">og:title</span> <span class="text-text ml-2">${esc(r.title)}</span></div>
            <div><span class="text-text-dim">og:description</span> <span class="text-text ml-2">${esc(r.description)}</span></div>
            <div><span class="text-text-dim">og:site_name</span> <span class="text-text ml-2">${esc(r.siteName)}</span></div>
            <div><span class="text-text-dim">og:url</span> <span class="text-text ml-2">${esc(r.url)}</span></div>
            <div><span class="text-text-dim">og:locale</span> <span class="text-text ml-2">${esc(r.locale)}</span></div>
            <div><span class="text-text-dim">og:image</span> <span class="text-text ml-2">${esc(r.image || '(none)')}</span></div>
            <div><span class="text-text-dim">twitter:card</span> <span class="text-text ml-2">${esc(r.twitterCard)}</span></div>
          </div>
        </section>
      </div>`;

      // Save meta text fields
      (content.querySelector('#meta-form') as HTMLFormElement).addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = (e.target as HTMLFormElement).querySelector('button[type="submit"]') as HTMLButtonElement;
        btn.disabled = true;
        const settings: Record<string, string> = {};
        content.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-meta]').forEach(el => {
          const key = el.dataset.meta!;
          const val = el.value.trim();
          if (val) settings[key] = val;
        });
        api.post('/admin/meta', { settings }).then(() => { showToast('Meta saved'); load(); }).catch(() => { showToast('Failed', 'err'); btn.disabled = false; });
      });

      // OG image upload
      content.querySelector('#og-upload')?.addEventListener('change', (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        api.post('/admin/meta/image', fd).then(() => { showToast('Image uploaded'); load(); }).catch(() => showToast('Upload failed', 'err'));
      });

      // OG image remove
      content.querySelector('#og-remove')?.addEventListener('click', () => {
        api.del('/admin/meta/image').then(() => { showToast('Image removed'); load(); }).catch(() => showToast('Failed', 'err'));
      });

      // Icon uploads
      content.querySelectorAll<HTMLInputElement>('[data-icon-slot]').forEach(input => {
        input.addEventListener('change', () => {
          const file = input.files?.[0];
          if (!file) return;
          const slot = input.dataset.iconSlot!;
          const fd = new FormData();
          fd.append('file', file);
          api.post(`/admin/meta/icons/${slot}`, fd).then(() => { showToast(`${slot} uploaded`); load(); }).catch(() => showToast('Upload failed — check dimensions', 'err'));
        });
      });

      // Icon removes
      content.querySelectorAll<HTMLButtonElement>('[data-icon-remove]').forEach(btn => {
        btn.addEventListener('click', () => {
          const slot = btn.dataset.iconRemove!;
          api.del(`/admin/meta/icons/${slot}`).then(() => { showToast(`${slot} removed`); load(); }).catch(() => showToast('Failed', 'err'));
        });
      });
    }

    function load() {
      api.get<MetaResponse>('/admin/meta').then(render).catch(() => {
        if (disposed) return;
        content.innerHTML = '<div class="py-6 text-center text-error">Failed to load meta tags</div>';
      });
    }

    load();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // API KEYS
  // ═══════════════════════════════════════════════════════════════════════════

  interface ApiKeyRow { id: string; label: string; prefix: string; createdAt: string; lastUsedAt: string | null; createdBy: { username: string; email: string } }

  function renderApiKeys() {
    content.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';

    function render(keys: ApiKeyRow[]) {
      if (disposed) return;
      content.innerHTML = `<div class="space-y-6">
        <div class="flex items-center justify-between">
          <h2 class="${SECTION_TITLE}">API Keys</h2>
          <button id="ak-create" class="${BTN}">Create Key</button>
        </div>
        ${keys.length === 0
          ? '<div class="rounded border border-gold/10 bg-surface px-6 py-10 text-center font-body text-sm text-text-dim">No API keys yet. Create one to allow MAD or external systems to connect.</div>'
          : `<div class="overflow-x-auto"><table class="w-full text-left"><thead><tr>
              <th class="${TH}">Label</th>
              <th class="${TH}">Prefix</th>
              <th class="${TH}">Created</th>
              <th class="${TH}">Last Used</th>
              <th class="${TH}">Created By</th>
              <th class="${TH}"></th>
            </tr></thead><tbody>${keys.map(k => `<tr>
              <td class="${TD} font-heading text-text">${esc(k.label)}</td>
              <td class="${TD}"><code class="rounded bg-bg px-1.5 py-0.5 font-mono text-xs text-gold">${esc(k.prefix)}...</code></td>
              <td class="${TD}">${fmtDate(k.createdAt)}</td>
              <td class="${TD}">${k.lastUsedAt ? fmtDate(k.lastUsedAt) : '<span class="text-text-dim">Never</span>'}</td>
              <td class="${TD}">${esc(k.createdBy?.username ?? '—')}</td>
              <td class="${TD}"><button data-del="${k.id}" class="${BTN_DANGER}">Revoke</button></td>
            </tr>`).join('')}</tbody></table></div>`}
      </div>`;

      content.querySelector('#ak-create')!.addEventListener('click', openCreateModal);
      content.querySelectorAll<HTMLButtonElement>('[data-del]').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.del!;
          if (!confirm('Revoke this API key? Any system using it will lose access.')) return;
          btn.disabled = true;
          api.del(`/admin/api-keys/${id}`).then(() => { showToast('Key revoked'); load(); }).catch(() => { showToast('Failed to revoke', 'err'); btn.disabled = false; });
        });
      });
    }

    function openCreateModal() {
      const { body, close } = adminModal(root, 'Create API Key', `
        <div class="space-y-4">
          <div><label class="mb-1 block font-label text-[10px] tracking-[.14em] uppercase text-text-dim">Label</label>
            <input id="ak-label" class="${INPUT}" placeholder="e.g. MAD Production" maxlength="100" /></div>
          <button id="ak-submit" class="${BTN} w-full">Generate Key</button>
          <div id="ak-result" class="hidden space-y-3">
            <div class="rounded border border-gold/20 bg-bg p-4">
              <div class="mb-1 font-label text-[10px] tracking-[.14em] uppercase text-text-dim">API Key (copy now — shown only once)</div>
              <code id="ak-key" class="block break-all font-mono text-xs text-gold select-all"></code>
            </div>
            <button id="ak-copy" class="${BTN_OUTLINE} w-full">Copy to Clipboard</button>
          </div>
        </div>`);

      const labelInput = body.querySelector<HTMLInputElement>('#ak-label')!;
      const submitBtn = body.querySelector<HTMLButtonElement>('#ak-submit')!;
      const resultDiv = body.querySelector<HTMLDivElement>('#ak-result')!;
      const keyCode = body.querySelector<HTMLElement>('#ak-key')!;
      const copyBtn = body.querySelector<HTMLButtonElement>('#ak-copy')!;

      submitBtn.addEventListener('click', () => {
        const label = labelInput.value.trim();
        if (!label) { showToast('Label required', 'err'); return; }
        submitBtn.disabled = true; submitBtn.textContent = 'Generating...';
        api.post<{ id: string; label: string; prefix: string; key: string }>('/admin/api-keys', { label }).then(res => {
          submitBtn.classList.add('hidden');
          labelInput.disabled = true;
          resultDiv.classList.remove('hidden');
          keyCode.textContent = res.key;
          copyBtn.addEventListener('click', () => {
            navigator.clipboard.writeText(res.key).then(() => showToast('Copied')).catch(() => showToast('Copy failed', 'err'));
          });
          body.closest('.fixed')!.querySelector('[data-modal-close]')!.addEventListener('click', () => { close(); load(); });
        }).catch(() => { showToast('Failed to create key', 'err'); submitBtn.disabled = false; submitBtn.textContent = 'Generate Key'; });
      });
    }

    function load() {
      api.get<ListResponse<ApiKeyRow>>('/admin/api-keys?limit=100').then(listItems).then(render).catch(() => {
        if (disposed) return;
        content.innerHTML = '<div class="py-6 text-center text-error">Failed to load API keys</div>';
      });
    }

    load();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SETTINGS
  // ═══════════════════════════════════════════════════════════════════════════

  function renderSettings() {
    content.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';
    Promise.all([
      api.get<Record<string, string>>('/admin/settings').catch(() => ({} as Record<string, string>)),
      api.get<PaymentMethodRow[]>('/admin/payment-methods').catch(() => [] as PaymentMethodRow[]),
    ]).then(([settings, pmRows]) => {
      if (disposed) return;
      const g = (k: string) => settings[k] ?? '';
      const b = (k: string) => g(k) === 'true' || g(k) === '1';
      const toggle = (key: string, label: string, on: boolean) =>
        `<label class="flex items-center justify-between rounded border border-gold/20 bg-surface px-4 py-3 cursor-pointer"><span class="font-heading text-sm text-text">${label}</span><input type="checkbox" data-ft="${key}" ${on ? 'checked' : ''} class="h-4 w-4 accent-gold" /></label>`;
      const inp = (label: string, key: string, val: string) =>
        `<div><label class="${STAT_LABEL}">${label.toUpperCase()}</label><input data-set="${key}" type="text" value="${esc(val)}" class="${INPUT} mt-1" /></div>`;

      const pmGrid = pmRows.map((pm) =>
        `<label class="flex items-center gap-2 rounded border border-gold/20 bg-surface px-3 py-2 cursor-pointer" title="${esc(pm.slug)}">
          <input type="checkbox" data-pm-slug="${esc(pm.slug)}" ${pm.enabled ? 'checked' : ''} class="h-4 w-4 accent-gold" />
          <span class="font-body text-sm text-text truncate">${esc(pm.name)}</span>
        </label>`
      ).join('');

      const enabledCount = pmRows.filter((pm) => pm.enabled).length;
      const allSlugs = pmRows.map((pm) => pm.slug);

      content.innerHTML = `<div class="space-y-8">
        <section><h2 class="${SECTION_TITLE}">Feature Toggles</h2><div class="space-y-2">
          ${toggle('feature.kyc', 'KYC Verification', b('feature.kyc'))}
          ${toggle('feature.withdrawals', 'Withdrawals', b('feature.withdrawals'))}
          ${toggle('feature.invoices', 'Invoices', b('feature.invoices'))}
        </div></section>
        <section>
          <div class="mb-3 flex items-center justify-between">
            <h2 class="${SECTION_TITLE} !mb-0">Payment Methods</h2>
            <div class="flex items-center gap-2">
              <span class="font-label text-[10px] tracking-wider text-text-dim">${enabledCount}/${pmRows.length} enabled</span>
              <button id="pm-enable-all" class="${BTN_OUTLINE}">Enable All</button>
              <button id="pm-disable-all" class="${BTN_OUTLINE}">Disable All</button>
            </div>
          </div>
          <div id="pm-grid" class="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            ${pmGrid}
          </div>
        </section>
        <section><h2 class="${SECTION_TITLE}">Company Info</h2><form id="s-company" class="space-y-3">
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            ${inp('Company Name', 'company.name', g('company.name'))}
            ${inp('Email', 'company.email', g('company.email'))}
            ${inp('Address', 'company.address', g('company.address'))}
            ${inp('Registration', 'company.registration', g('company.registration'))}
            ${inp('Jurisdiction', 'company.jurisdiction', g('company.jurisdiction'))}
            ${inp('Country', 'company.country', g('company.country'))}
            ${inp('Domain', 'company.domain', g('company.domain'))}
            ${inp('Game Name', 'company.game_name', g('company.game_name'))}
          </div>
          <div class="flex justify-end"><button type="submit" class="${BTN}">Save Company Info</button></div>
        </form></section>
      </div>`;

      // Feature toggles
      content.querySelectorAll<HTMLInputElement>('[data-ft]').forEach((cb) => {
        cb.addEventListener('change', () => {
          api.post('/admin/settings', { key: cb.dataset.ft!, value: cb.checked ? 'true' : 'false' })
            .then(() => showToast(`${cb.dataset.ft} updated`)).catch(() => { showToast('Failed', 'err'); cb.checked = !cb.checked; });
        });
      });

      // Payment method individual toggles
      content.querySelectorAll<HTMLInputElement>('[data-pm-slug]').forEach((cb) => {
        cb.addEventListener('change', () => {
          api.post('/admin/payment-methods/toggle', { slug: cb.dataset.pmSlug!, enabled: cb.checked })
            .then(() => showToast(`${cb.dataset.pmSlug} ${cb.checked ? 'enabled' : 'disabled'}`))
            .catch(() => { showToast('Failed', 'err'); cb.checked = !cb.checked; });
        });
      });

      // Enable All
      content.querySelector('#pm-enable-all')!.addEventListener('click', () => {
        const btn = content.querySelector('#pm-enable-all') as HTMLButtonElement;
        btn.disabled = true;
        api.post('/admin/payment-methods/bulk-toggle', { slugs: allSlugs, enabled: true })
          .then(() => { showToast('All enabled'); renderSettings(); })
          .catch(() => { showToast('Failed', 'err'); btn.disabled = false; });
      });

      // Disable All
      content.querySelector('#pm-disable-all')!.addEventListener('click', () => {
        const btn = content.querySelector('#pm-disable-all') as HTMLButtonElement;
        btn.disabled = true;
        api.post('/admin/payment-methods/bulk-toggle', { slugs: allSlugs, enabled: false })
          .then(() => { showToast('All disabled'); renderSettings(); })
          .catch(() => { showToast('Failed', 'err'); btn.disabled = false; });
      });

      // Company form
      (content.querySelector('#s-company') as HTMLFormElement).addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = (e.target as HTMLFormElement).querySelector('button[type="submit"]') as HTMLButtonElement;
        btn.disabled = true;
        const s: Record<string, string> = {};
        content.querySelectorAll<HTMLInputElement>('[data-set]').forEach((i) => { s[i.dataset.set!.replace('company.', '')] = i.value; });
        api.post('/admin/settings/company', { settings: s }).then(() => { showToast('Saved'); btn.disabled = false; }).catch(() => { showToast('Failed', 'err'); btn.disabled = false; });
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SERVER
  // ═══════════════════════════════════════════════════════════════════════════

  function renderServer() {
    content.innerHTML = '<div class="py-6 text-center text-text-dim">Loading...</div>';
    Promise.all([
      api.get<ServerStatus>('/admin/server-status').catch(() => null),
      api.get<{ count: number }>('/admin/bots').catch(() => null),
    ]).then(([server, bots]) => {
      if (disposed) return;
      content.innerHTML = `<div class="space-y-6">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div class="${STAT_CARD}"><div class="${STAT_LABEL}">DB STATUS</div><div class="mt-1 font-heading text-2xl ${server?.dbHealthy ? 'text-success' : 'text-error'}">${server?.dbHealthy ? 'Healthy' : 'Down'}</div></div>
          <div class="${STAT_CARD}"><div class="${STAT_LABEL}">UPTIME</div><div class="${STAT_NUM}">${server ? fmtUptime(server.uptimeSeconds) : '—'}</div></div>
          <div class="${STAT_CARD}"><div class="${STAT_LABEL}">MEMORY (RSS)</div><div class="${STAT_NUM}">${server ? server.memoryMb + ' MB' : '—'}</div></div>
          <div class="${STAT_CARD}"><div class="${STAT_LABEL}">NODE</div><div class="${STAT_NUM}">${server ? esc(server.nodeVersion) : '—'}</div></div>
        </div>
        <section><h2 class="${SECTION_TITLE}">Bots</h2><div class="flex items-center gap-4">
          <div class="rounded border border-gold/20 bg-surface px-4 py-3"><span class="${STAT_LABEL}">COUNT</span><span class="ml-3 font-heading text-lg text-gold">${bots?.count ?? '—'}</span></div>
          <button id="bot-tick" class="${BTN}">Trigger Bot Tick</button>
        </div></section>
      </div>`;
      content.querySelector('#bot-tick')!.addEventListener('click', () => {
        const btn = content.querySelector('#bot-tick') as HTMLButtonElement;
        btn.disabled = true; btn.textContent = 'Running...';
        api.post('/admin/bots/tick', {}).then(() => { showToast('Bot tick done'); btn.textContent = 'Trigger Bot Tick'; btn.disabled = false; }).catch(() => { showToast('Failed', 'err'); btn.textContent = 'Trigger Bot Tick'; btn.disabled = false; });
      });
    });
  }

  // ── Hash routing ────────────────────────────────────────────────────────────
  function onHashChange() {
    const hash = location.hash.replace('#', '');
    const tab = SLUG_TO_TAB[hash] ?? 'Dashboard';
    if (tab !== activeTab) switchTab(tab, false);
  }
  if (!sandboxMode) window.addEventListener('hashchange', onHashChange);

  // ── Boot ────────────────────────────────────────────────────────────────────
  renderTab();
  return () => {
    disposed = true;
    clearInterval(dashRefreshTimer);
    clearInterval(dashAgoTimer);
    if (!sandboxMode) window.removeEventListener('hashchange', onHashChange);
  };
}
